import {
  Cell,
  Slice,
  Address,
  Builder,
  beginCell,
  ComputeError,
  TupleItem,
  TupleReader,
  Dictionary,
  contractAddress,
  ContractProvider,
  Sender,
  Contract,
  ContractABI,
  ABIType,
  ABIGetter,
  ABIReceiver,
  TupleBuilder,
  DictionaryValue,
} from '@ton/core';

export type StateInit = {
  $$type: 'StateInit';
  code: Cell;
  data: Cell;
};

export function storeStateInit(src: StateInit) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeRef(src.code);
    b_0.storeRef(src.data);
  };
}

export function loadStateInit(slice: Slice) {
  let sc_0 = slice;
  let _code = sc_0.loadRef();
  let _data = sc_0.loadRef();
  return { $$type: 'StateInit' as const, code: _code, data: _data };
}

function loadTupleStateInit(source: TupleReader) {
  let _code = source.readCell();
  let _data = source.readCell();
  return { $$type: 'StateInit' as const, code: _code, data: _data };
}

function loadGetterTupleStateInit(source: TupleReader) {
  let _code = source.readCell();
  let _data = source.readCell();
  return { $$type: 'StateInit' as const, code: _code, data: _data };
}

function storeTupleStateInit(source: StateInit) {
  let builder = new TupleBuilder();
  builder.writeCell(source.code);
  builder.writeCell(source.data);
  return builder.build();
}

function dictValueParserStateInit(): DictionaryValue<StateInit> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeStateInit(src)).endCell());
    },
    parse: (src) => {
      return loadStateInit(src.loadRef().beginParse());
    },
  };
}

export type StdAddress = {
  $$type: 'StdAddress';
  workchain: bigint;
  address: bigint;
};

export function storeStdAddress(src: StdAddress) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeInt(src.workchain, 8);
    b_0.storeUint(src.address, 256);
  };
}

export function loadStdAddress(slice: Slice) {
  let sc_0 = slice;
  let _workchain = sc_0.loadIntBig(8);
  let _address = sc_0.loadUintBig(256);
  return {
    $$type: 'StdAddress' as const,
    workchain: _workchain,
    address: _address,
  };
}

function loadTupleStdAddress(source: TupleReader) {
  let _workchain = source.readBigNumber();
  let _address = source.readBigNumber();
  return {
    $$type: 'StdAddress' as const,
    workchain: _workchain,
    address: _address,
  };
}

function loadGetterTupleStdAddress(source: TupleReader) {
  let _workchain = source.readBigNumber();
  let _address = source.readBigNumber();
  return {
    $$type: 'StdAddress' as const,
    workchain: _workchain,
    address: _address,
  };
}

function storeTupleStdAddress(source: StdAddress) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.workchain);
  builder.writeNumber(source.address);
  return builder.build();
}

function dictValueParserStdAddress(): DictionaryValue<StdAddress> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeStdAddress(src)).endCell());
    },
    parse: (src) => {
      return loadStdAddress(src.loadRef().beginParse());
    },
  };
}

export type VarAddress = {
  $$type: 'VarAddress';
  workchain: bigint;
  address: Slice;
};

export function storeVarAddress(src: VarAddress) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeInt(src.workchain, 32);
    b_0.storeRef(src.address.asCell());
  };
}

export function loadVarAddress(slice: Slice) {
  let sc_0 = slice;
  let _workchain = sc_0.loadIntBig(32);
  let _address = sc_0.loadRef().asSlice();
  return {
    $$type: 'VarAddress' as const,
    workchain: _workchain,
    address: _address,
  };
}

function loadTupleVarAddress(source: TupleReader) {
  let _workchain = source.readBigNumber();
  let _address = source.readCell().asSlice();
  return {
    $$type: 'VarAddress' as const,
    workchain: _workchain,
    address: _address,
  };
}

function loadGetterTupleVarAddress(source: TupleReader) {
  let _workchain = source.readBigNumber();
  let _address = source.readCell().asSlice();
  return {
    $$type: 'VarAddress' as const,
    workchain: _workchain,
    address: _address,
  };
}

function storeTupleVarAddress(source: VarAddress) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.workchain);
  builder.writeSlice(source.address.asCell());
  return builder.build();
}

function dictValueParserVarAddress(): DictionaryValue<VarAddress> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeVarAddress(src)).endCell());
    },
    parse: (src) => {
      return loadVarAddress(src.loadRef().beginParse());
    },
  };
}

export type Context = {
  $$type: 'Context';
  bounced: boolean;
  sender: Address;
  value: bigint;
  raw: Slice;
};

export function storeContext(src: Context) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeBit(src.bounced);
    b_0.storeAddress(src.sender);
    b_0.storeInt(src.value, 257);
    b_0.storeRef(src.raw.asCell());
  };
}

export function loadContext(slice: Slice) {
  let sc_0 = slice;
  let _bounced = sc_0.loadBit();
  let _sender = sc_0.loadAddress();
  let _value = sc_0.loadIntBig(257);
  let _raw = sc_0.loadRef().asSlice();
  return {
    $$type: 'Context' as const,
    bounced: _bounced,
    sender: _sender,
    value: _value,
    raw: _raw,
  };
}

function loadTupleContext(source: TupleReader) {
  let _bounced = source.readBoolean();
  let _sender = source.readAddress();
  let _value = source.readBigNumber();
  let _raw = source.readCell().asSlice();
  return {
    $$type: 'Context' as const,
    bounced: _bounced,
    sender: _sender,
    value: _value,
    raw: _raw,
  };
}

function loadGetterTupleContext(source: TupleReader) {
  let _bounced = source.readBoolean();
  let _sender = source.readAddress();
  let _value = source.readBigNumber();
  let _raw = source.readCell().asSlice();
  return {
    $$type: 'Context' as const,
    bounced: _bounced,
    sender: _sender,
    value: _value,
    raw: _raw,
  };
}

function storeTupleContext(source: Context) {
  let builder = new TupleBuilder();
  builder.writeBoolean(source.bounced);
  builder.writeAddress(source.sender);
  builder.writeNumber(source.value);
  builder.writeSlice(source.raw.asCell());
  return builder.build();
}

function dictValueParserContext(): DictionaryValue<Context> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeContext(src)).endCell());
    },
    parse: (src) => {
      return loadContext(src.loadRef().beginParse());
    },
  };
}

export type SendParameters = {
  $$type: 'SendParameters';
  bounce: boolean;
  to: Address;
  value: bigint;
  mode: bigint;
  body: Cell | null;
  code: Cell | null;
  data: Cell | null;
};

export function storeSendParameters(src: SendParameters) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeBit(src.bounce);
    b_0.storeAddress(src.to);
    b_0.storeInt(src.value, 257);
    b_0.storeInt(src.mode, 257);
    if (src.body !== null && src.body !== undefined) {
      b_0.storeBit(true).storeRef(src.body);
    } else {
      b_0.storeBit(false);
    }
    if (src.code !== null && src.code !== undefined) {
      b_0.storeBit(true).storeRef(src.code);
    } else {
      b_0.storeBit(false);
    }
    if (src.data !== null && src.data !== undefined) {
      b_0.storeBit(true).storeRef(src.data);
    } else {
      b_0.storeBit(false);
    }
  };
}

export function loadSendParameters(slice: Slice) {
  let sc_0 = slice;
  let _bounce = sc_0.loadBit();
  let _to = sc_0.loadAddress();
  let _value = sc_0.loadIntBig(257);
  let _mode = sc_0.loadIntBig(257);
  let _body = sc_0.loadBit() ? sc_0.loadRef() : null;
  let _code = sc_0.loadBit() ? sc_0.loadRef() : null;
  let _data = sc_0.loadBit() ? sc_0.loadRef() : null;
  return {
    $$type: 'SendParameters' as const,
    bounce: _bounce,
    to: _to,
    value: _value,
    mode: _mode,
    body: _body,
    code: _code,
    data: _data,
  };
}

function loadTupleSendParameters(source: TupleReader) {
  let _bounce = source.readBoolean();
  let _to = source.readAddress();
  let _value = source.readBigNumber();
  let _mode = source.readBigNumber();
  let _body = source.readCellOpt();
  let _code = source.readCellOpt();
  let _data = source.readCellOpt();
  return {
    $$type: 'SendParameters' as const,
    bounce: _bounce,
    to: _to,
    value: _value,
    mode: _mode,
    body: _body,
    code: _code,
    data: _data,
  };
}

function loadGetterTupleSendParameters(source: TupleReader) {
  let _bounce = source.readBoolean();
  let _to = source.readAddress();
  let _value = source.readBigNumber();
  let _mode = source.readBigNumber();
  let _body = source.readCellOpt();
  let _code = source.readCellOpt();
  let _data = source.readCellOpt();
  return {
    $$type: 'SendParameters' as const,
    bounce: _bounce,
    to: _to,
    value: _value,
    mode: _mode,
    body: _body,
    code: _code,
    data: _data,
  };
}

function storeTupleSendParameters(source: SendParameters) {
  let builder = new TupleBuilder();
  builder.writeBoolean(source.bounce);
  builder.writeAddress(source.to);
  builder.writeNumber(source.value);
  builder.writeNumber(source.mode);
  builder.writeCell(source.body);
  builder.writeCell(source.code);
  builder.writeCell(source.data);
  return builder.build();
}

function dictValueParserSendParameters(): DictionaryValue<SendParameters> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeSendParameters(src)).endCell());
    },
    parse: (src) => {
      return loadSendParameters(src.loadRef().beginParse());
    },
  };
}

export type Deploy = {
  $$type: 'Deploy';
  queryId: bigint;
};

export function storeDeploy(src: Deploy) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(2490013878, 32);
    b_0.storeUint(src.queryId, 64);
  };
}

export function loadDeploy(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 2490013878) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  return { $$type: 'Deploy' as const, queryId: _queryId };
}

function loadTupleDeploy(source: TupleReader) {
  let _queryId = source.readBigNumber();
  return { $$type: 'Deploy' as const, queryId: _queryId };
}

function loadGetterTupleDeploy(source: TupleReader) {
  let _queryId = source.readBigNumber();
  return { $$type: 'Deploy' as const, queryId: _queryId };
}

function storeTupleDeploy(source: Deploy) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  return builder.build();
}

function dictValueParserDeploy(): DictionaryValue<Deploy> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeDeploy(src)).endCell());
    },
    parse: (src) => {
      return loadDeploy(src.loadRef().beginParse());
    },
  };
}

export type DeployOk = {
  $$type: 'DeployOk';
  queryId: bigint;
};

export function storeDeployOk(src: DeployOk) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(2952335191, 32);
    b_0.storeUint(src.queryId, 64);
  };
}

export function loadDeployOk(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 2952335191) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  return { $$type: 'DeployOk' as const, queryId: _queryId };
}

function loadTupleDeployOk(source: TupleReader) {
  let _queryId = source.readBigNumber();
  return { $$type: 'DeployOk' as const, queryId: _queryId };
}

function loadGetterTupleDeployOk(source: TupleReader) {
  let _queryId = source.readBigNumber();
  return { $$type: 'DeployOk' as const, queryId: _queryId };
}

function storeTupleDeployOk(source: DeployOk) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  return builder.build();
}

function dictValueParserDeployOk(): DictionaryValue<DeployOk> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeDeployOk(src)).endCell());
    },
    parse: (src) => {
      return loadDeployOk(src.loadRef().beginParse());
    },
  };
}

export type FactoryDeploy = {
  $$type: 'FactoryDeploy';
  queryId: bigint;
  cashback: Address;
};

export function storeFactoryDeploy(src: FactoryDeploy) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(1829761339, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeAddress(src.cashback);
  };
}

export function loadFactoryDeploy(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 1829761339) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _cashback = sc_0.loadAddress();
  return {
    $$type: 'FactoryDeploy' as const,
    queryId: _queryId,
    cashback: _cashback,
  };
}

function loadTupleFactoryDeploy(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _cashback = source.readAddress();
  return {
    $$type: 'FactoryDeploy' as const,
    queryId: _queryId,
    cashback: _cashback,
  };
}

function loadGetterTupleFactoryDeploy(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _cashback = source.readAddress();
  return {
    $$type: 'FactoryDeploy' as const,
    queryId: _queryId,
    cashback: _cashback,
  };
}

function storeTupleFactoryDeploy(source: FactoryDeploy) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeAddress(source.cashback);
  return builder.build();
}

function dictValueParserFactoryDeploy(): DictionaryValue<FactoryDeploy> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeFactoryDeploy(src)).endCell());
    },
    parse: (src) => {
      return loadFactoryDeploy(src.loadRef().beginParse());
    },
  };
}

export type TokenTransfer = {
  $$type: 'TokenTransfer';
  queryId: bigint;
  amount: bigint;
  destination: Address;
  response_destination: Address;
  custom_payload: Cell | null;
  forward_ton_amount: bigint;
  forward_payload: Slice;
};

export function storeTokenTransfer(src: TokenTransfer) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(260734629, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeCoins(src.amount);
    b_0.storeAddress(src.destination);
    b_0.storeAddress(src.response_destination);
    if (src.custom_payload !== null && src.custom_payload !== undefined) {
      b_0.storeBit(true).storeRef(src.custom_payload);
    } else {
      b_0.storeBit(false);
    }
    b_0.storeCoins(src.forward_ton_amount);
    b_0.storeBuilder(src.forward_payload.asBuilder());
  };
}

export function loadTokenTransfer(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 260734629) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _amount = sc_0.loadCoins();
  let _destination = sc_0.loadAddress();
  let _response_destination = sc_0.loadAddress();
  let _custom_payload = sc_0.loadBit() ? sc_0.loadRef() : null;
  let _forward_ton_amount = sc_0.loadCoins();
  let _forward_payload = sc_0;
  return {
    $$type: 'TokenTransfer' as const,
    queryId: _queryId,
    amount: _amount,
    destination: _destination,
    response_destination: _response_destination,
    custom_payload: _custom_payload,
    forward_ton_amount: _forward_ton_amount,
    forward_payload: _forward_payload,
  };
}

function loadTupleTokenTransfer(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _destination = source.readAddress();
  let _response_destination = source.readAddress();
  let _custom_payload = source.readCellOpt();
  let _forward_ton_amount = source.readBigNumber();
  let _forward_payload = source.readCell().asSlice();
  return {
    $$type: 'TokenTransfer' as const,
    queryId: _queryId,
    amount: _amount,
    destination: _destination,
    response_destination: _response_destination,
    custom_payload: _custom_payload,
    forward_ton_amount: _forward_ton_amount,
    forward_payload: _forward_payload,
  };
}

function loadGetterTupleTokenTransfer(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _destination = source.readAddress();
  let _response_destination = source.readAddress();
  let _custom_payload = source.readCellOpt();
  let _forward_ton_amount = source.readBigNumber();
  let _forward_payload = source.readCell().asSlice();
  return {
    $$type: 'TokenTransfer' as const,
    queryId: _queryId,
    amount: _amount,
    destination: _destination,
    response_destination: _response_destination,
    custom_payload: _custom_payload,
    forward_ton_amount: _forward_ton_amount,
    forward_payload: _forward_payload,
  };
}

function storeTupleTokenTransfer(source: TokenTransfer) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeNumber(source.amount);
  builder.writeAddress(source.destination);
  builder.writeAddress(source.response_destination);
  builder.writeCell(source.custom_payload);
  builder.writeNumber(source.forward_ton_amount);
  builder.writeSlice(source.forward_payload.asCell());
  return builder.build();
}

function dictValueParserTokenTransfer(): DictionaryValue<TokenTransfer> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeTokenTransfer(src)).endCell());
    },
    parse: (src) => {
      return loadTokenTransfer(src.loadRef().beginParse());
    },
  };
}

export type TokenTransferInternal = {
  $$type: 'TokenTransferInternal';
  queryId: bigint;
  amount: bigint;
  from: Address;
  response_destination: Address;
  forward_ton_amount: bigint;
  jettonContractAddress: Address | null;
  forward_payload: Slice;
};

export function storeTokenTransferInternal(src: TokenTransferInternal) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(395134233, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeCoins(src.amount);
    b_0.storeAddress(src.from);
    b_0.storeAddress(src.response_destination);
    b_0.storeCoins(src.forward_ton_amount);
    let b_1 = new Builder();
    b_1.storeAddress(src.jettonContractAddress);
    b_1.storeBuilder(src.forward_payload.asBuilder());
    b_0.storeRef(b_1.endCell());
  };
}

export function loadTokenTransferInternal(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 395134233) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _amount = sc_0.loadCoins();
  let _from = sc_0.loadAddress();
  let _response_destination = sc_0.loadAddress();
  let _forward_ton_amount = sc_0.loadCoins();
  let sc_1 = sc_0.loadRef().beginParse();
  let _jettonContractAddress = sc_1.loadMaybeAddress();
  let _forward_payload = sc_1;
  return {
    $$type: 'TokenTransferInternal' as const,
    queryId: _queryId,
    amount: _amount,
    from: _from,
    response_destination: _response_destination,
    forward_ton_amount: _forward_ton_amount,
    jettonContractAddress: _jettonContractAddress,
    forward_payload: _forward_payload,
  };
}

function loadTupleTokenTransferInternal(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _from = source.readAddress();
  let _response_destination = source.readAddress();
  let _forward_ton_amount = source.readBigNumber();
  let _jettonContractAddress = source.readAddressOpt();
  let _forward_payload = source.readCell().asSlice();
  return {
    $$type: 'TokenTransferInternal' as const,
    queryId: _queryId,
    amount: _amount,
    from: _from,
    response_destination: _response_destination,
    forward_ton_amount: _forward_ton_amount,
    jettonContractAddress: _jettonContractAddress,
    forward_payload: _forward_payload,
  };
}

function loadGetterTupleTokenTransferInternal(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _from = source.readAddress();
  let _response_destination = source.readAddress();
  let _forward_ton_amount = source.readBigNumber();
  let _jettonContractAddress = source.readAddressOpt();
  let _forward_payload = source.readCell().asSlice();
  return {
    $$type: 'TokenTransferInternal' as const,
    queryId: _queryId,
    amount: _amount,
    from: _from,
    response_destination: _response_destination,
    forward_ton_amount: _forward_ton_amount,
    jettonContractAddress: _jettonContractAddress,
    forward_payload: _forward_payload,
  };
}

function storeTupleTokenTransferInternal(source: TokenTransferInternal) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeNumber(source.amount);
  builder.writeAddress(source.from);
  builder.writeAddress(source.response_destination);
  builder.writeNumber(source.forward_ton_amount);
  builder.writeAddress(source.jettonContractAddress);
  builder.writeSlice(source.forward_payload.asCell());
  return builder.build();
}

function dictValueParserTokenTransferInternal(): DictionaryValue<TokenTransferInternal> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeTokenTransferInternal(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadTokenTransferInternal(src.loadRef().beginParse());
    },
  };
}

export type TokenNotification = {
  $$type: 'TokenNotification';
  queryId: bigint;
  amount: bigint;
  from: Address;
  forward_payload: Slice;
};

export function storeTokenNotification(src: TokenNotification) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(1935855772, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeCoins(src.amount);
    b_0.storeAddress(src.from);
    b_0.storeBuilder(src.forward_payload.asBuilder());
  };
}

export function loadTokenNotification(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 1935855772) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _amount = sc_0.loadCoins();
  let _from = sc_0.loadAddress();
  let _forward_payload = sc_0;
  return {
    $$type: 'TokenNotification' as const,
    queryId: _queryId,
    amount: _amount,
    from: _from,
    forward_payload: _forward_payload,
  };
}

function loadTupleTokenNotification(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _from = source.readAddress();
  let _forward_payload = source.readCell().asSlice();
  return {
    $$type: 'TokenNotification' as const,
    queryId: _queryId,
    amount: _amount,
    from: _from,
    forward_payload: _forward_payload,
  };
}

function loadGetterTupleTokenNotification(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _from = source.readAddress();
  let _forward_payload = source.readCell().asSlice();
  return {
    $$type: 'TokenNotification' as const,
    queryId: _queryId,
    amount: _amount,
    from: _from,
    forward_payload: _forward_payload,
  };
}

function storeTupleTokenNotification(source: TokenNotification) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeNumber(source.amount);
  builder.writeAddress(source.from);
  builder.writeSlice(source.forward_payload.asCell());
  return builder.build();
}

function dictValueParserTokenNotification(): DictionaryValue<TokenNotification> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeTokenNotification(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadTokenNotification(src.loadRef().beginParse());
    },
  };
}

export type TransferNotification = {
  $$type: 'TransferNotification';
  queryId: bigint;
  amount: bigint;
  sender: Address;
  forwardPayload: Cell | null;
};

export function storeTransferNotification(src: TransferNotification) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(1935855773, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeCoins(src.amount);
    b_0.storeAddress(src.sender);
    if (src.forwardPayload !== null && src.forwardPayload !== undefined) {
      b_0.storeBit(true).storeRef(src.forwardPayload);
    } else {
      b_0.storeBit(false);
    }
  };
}

export function loadTransferNotification(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 1935855773) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _amount = sc_0.loadCoins();
  let _sender = sc_0.loadAddress();
  let _forwardPayload = sc_0.loadBit() ? sc_0.loadRef() : null;
  return {
    $$type: 'TransferNotification' as const,
    queryId: _queryId,
    amount: _amount,
    sender: _sender,
    forwardPayload: _forwardPayload,
  };
}

function loadTupleTransferNotification(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _sender = source.readAddress();
  let _forwardPayload = source.readCellOpt();
  return {
    $$type: 'TransferNotification' as const,
    queryId: _queryId,
    amount: _amount,
    sender: _sender,
    forwardPayload: _forwardPayload,
  };
}

function loadGetterTupleTransferNotification(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _sender = source.readAddress();
  let _forwardPayload = source.readCellOpt();
  return {
    $$type: 'TransferNotification' as const,
    queryId: _queryId,
    amount: _amount,
    sender: _sender,
    forwardPayload: _forwardPayload,
  };
}

function storeTupleTransferNotification(source: TransferNotification) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeNumber(source.amount);
  builder.writeAddress(source.sender);
  builder.writeCell(source.forwardPayload);
  return builder.build();
}

function dictValueParserTransferNotification(): DictionaryValue<TransferNotification> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeTransferNotification(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadTransferNotification(src.loadRef().beginParse());
    },
  };
}

export type TokenBurn = {
  $$type: 'TokenBurn';
  queryId: bigint;
  amount: bigint;
  owner: Address;
  response_destination: Address;
};

export function storeTokenBurn(src: TokenBurn) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(1499400124, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeCoins(src.amount);
    b_0.storeAddress(src.owner);
    b_0.storeAddress(src.response_destination);
  };
}

export function loadTokenBurn(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 1499400124) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _amount = sc_0.loadCoins();
  let _owner = sc_0.loadAddress();
  let _response_destination = sc_0.loadAddress();
  return {
    $$type: 'TokenBurn' as const,
    queryId: _queryId,
    amount: _amount,
    owner: _owner,
    response_destination: _response_destination,
  };
}

function loadTupleTokenBurn(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _owner = source.readAddress();
  let _response_destination = source.readAddress();
  return {
    $$type: 'TokenBurn' as const,
    queryId: _queryId,
    amount: _amount,
    owner: _owner,
    response_destination: _response_destination,
  };
}

function loadGetterTupleTokenBurn(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _owner = source.readAddress();
  let _response_destination = source.readAddress();
  return {
    $$type: 'TokenBurn' as const,
    queryId: _queryId,
    amount: _amount,
    owner: _owner,
    response_destination: _response_destination,
  };
}

function storeTupleTokenBurn(source: TokenBurn) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeNumber(source.amount);
  builder.writeAddress(source.owner);
  builder.writeAddress(source.response_destination);
  return builder.build();
}

function dictValueParserTokenBurn(): DictionaryValue<TokenBurn> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeTokenBurn(src)).endCell());
    },
    parse: (src) => {
      return loadTokenBurn(src.loadRef().beginParse());
    },
  };
}

export type TokenBurnForSell = {
  $$type: 'TokenBurnForSell';
  queryId: bigint;
  amount: bigint;
  owner: Address;
  response_destination: Address;
};

export function storeTokenBurnForSell(src: TokenBurnForSell) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(2401739732, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeCoins(src.amount);
    b_0.storeAddress(src.owner);
    b_0.storeAddress(src.response_destination);
  };
}

export function loadTokenBurnForSell(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 2401739732) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _amount = sc_0.loadCoins();
  let _owner = sc_0.loadAddress();
  let _response_destination = sc_0.loadAddress();
  return {
    $$type: 'TokenBurnForSell' as const,
    queryId: _queryId,
    amount: _amount,
    owner: _owner,
    response_destination: _response_destination,
  };
}

function loadTupleTokenBurnForSell(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _owner = source.readAddress();
  let _response_destination = source.readAddress();
  return {
    $$type: 'TokenBurnForSell' as const,
    queryId: _queryId,
    amount: _amount,
    owner: _owner,
    response_destination: _response_destination,
  };
}

function loadGetterTupleTokenBurnForSell(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _owner = source.readAddress();
  let _response_destination = source.readAddress();
  return {
    $$type: 'TokenBurnForSell' as const,
    queryId: _queryId,
    amount: _amount,
    owner: _owner,
    response_destination: _response_destination,
  };
}

function storeTupleTokenBurnForSell(source: TokenBurnForSell) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeNumber(source.amount);
  builder.writeAddress(source.owner);
  builder.writeAddress(source.response_destination);
  return builder.build();
}

function dictValueParserTokenBurnForSell(): DictionaryValue<TokenBurnForSell> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeTokenBurnForSell(src)).endCell());
    },
    parse: (src) => {
      return loadTokenBurnForSell(src.loadRef().beginParse());
    },
  };
}

export type TokensBurnt = {
  $$type: 'TokensBurnt';
  queryId: bigint;
  amount: bigint;
  tokenOwner: Address;
};

export function storeTokensBurnt(src: TokensBurnt) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(2401739733, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeCoins(src.amount);
    b_0.storeAddress(src.tokenOwner);
  };
}

export function loadTokensBurnt(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 2401739733) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _amount = sc_0.loadCoins();
  let _tokenOwner = sc_0.loadAddress();
  return {
    $$type: 'TokensBurnt' as const,
    queryId: _queryId,
    amount: _amount,
    tokenOwner: _tokenOwner,
  };
}

function loadTupleTokensBurnt(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _tokenOwner = source.readAddress();
  return {
    $$type: 'TokensBurnt' as const,
    queryId: _queryId,
    amount: _amount,
    tokenOwner: _tokenOwner,
  };
}

function loadGetterTupleTokensBurnt(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _tokenOwner = source.readAddress();
  return {
    $$type: 'TokensBurnt' as const,
    queryId: _queryId,
    amount: _amount,
    tokenOwner: _tokenOwner,
  };
}

function storeTupleTokensBurnt(source: TokensBurnt) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeNumber(source.amount);
  builder.writeAddress(source.tokenOwner);
  return builder.build();
}

function dictValueParserTokensBurnt(): DictionaryValue<TokensBurnt> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeTokensBurnt(src)).endCell());
    },
    parse: (src) => {
      return loadTokensBurnt(src.loadRef().beginParse());
    },
  };
}

export type TokenBurnNotification = {
  $$type: 'TokenBurnNotification';
  queryId: bigint;
  amount: bigint;
  owner: Address;
  response_destination: Address | null;
};

export function storeTokenBurnNotification(src: TokenBurnNotification) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(2078119902, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeCoins(src.amount);
    b_0.storeAddress(src.owner);
    b_0.storeAddress(src.response_destination);
  };
}

export function loadTokenBurnNotification(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 2078119902) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _amount = sc_0.loadCoins();
  let _owner = sc_0.loadAddress();
  let _response_destination = sc_0.loadMaybeAddress();
  return {
    $$type: 'TokenBurnNotification' as const,
    queryId: _queryId,
    amount: _amount,
    owner: _owner,
    response_destination: _response_destination,
  };
}

function loadTupleTokenBurnNotification(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _owner = source.readAddress();
  let _response_destination = source.readAddressOpt();
  return {
    $$type: 'TokenBurnNotification' as const,
    queryId: _queryId,
    amount: _amount,
    owner: _owner,
    response_destination: _response_destination,
  };
}

function loadGetterTupleTokenBurnNotification(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _owner = source.readAddress();
  let _response_destination = source.readAddressOpt();
  return {
    $$type: 'TokenBurnNotification' as const,
    queryId: _queryId,
    amount: _amount,
    owner: _owner,
    response_destination: _response_destination,
  };
}

function storeTupleTokenBurnNotification(source: TokenBurnNotification) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeNumber(source.amount);
  builder.writeAddress(source.owner);
  builder.writeAddress(source.response_destination);
  return builder.build();
}

function dictValueParserTokenBurnNotification(): DictionaryValue<TokenBurnNotification> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeTokenBurnNotification(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadTokenBurnNotification(src.loadRef().beginParse());
    },
  };
}

export type TokenExcesses = {
  $$type: 'TokenExcesses';
  queryId: bigint;
};

export function storeTokenExcesses(src: TokenExcesses) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(3576854235, 32);
    b_0.storeUint(src.queryId, 64);
  };
}

export function loadTokenExcesses(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 3576854235) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  return { $$type: 'TokenExcesses' as const, queryId: _queryId };
}

function loadTupleTokenExcesses(source: TupleReader) {
  let _queryId = source.readBigNumber();
  return { $$type: 'TokenExcesses' as const, queryId: _queryId };
}

function loadGetterTupleTokenExcesses(source: TupleReader) {
  let _queryId = source.readBigNumber();
  return { $$type: 'TokenExcesses' as const, queryId: _queryId };
}

function storeTupleTokenExcesses(source: TokenExcesses) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  return builder.build();
}

function dictValueParserTokenExcesses(): DictionaryValue<TokenExcesses> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeTokenExcesses(src)).endCell());
    },
    parse: (src) => {
      return loadTokenExcesses(src.loadRef().beginParse());
    },
  };
}

export type TokenRefundOnFail = {
  $$type: 'TokenRefundOnFail';
  queryId: bigint;
  reason: string;
};

export function storeTokenRefundOnFail(src: TokenRefundOnFail) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(2401739734, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeStringRefTail(src.reason);
  };
}

export function loadTokenRefundOnFail(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 2401739734) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _reason = sc_0.loadStringRefTail();
  return {
    $$type: 'TokenRefundOnFail' as const,
    queryId: _queryId,
    reason: _reason,
  };
}

function loadTupleTokenRefundOnFail(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _reason = source.readString();
  return {
    $$type: 'TokenRefundOnFail' as const,
    queryId: _queryId,
    reason: _reason,
  };
}

function loadGetterTupleTokenRefundOnFail(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _reason = source.readString();
  return {
    $$type: 'TokenRefundOnFail' as const,
    queryId: _queryId,
    reason: _reason,
  };
}

function storeTupleTokenRefundOnFail(source: TokenRefundOnFail) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeString(source.reason);
  return builder.build();
}

function dictValueParserTokenRefundOnFail(): DictionaryValue<TokenRefundOnFail> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeTokenRefundOnFail(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadTokenRefundOnFail(src.loadRef().beginParse());
    },
  };
}

export type ProvideWalletAddress = {
  $$type: 'ProvideWalletAddress';
  queryId: bigint;
  ownerAddress: Address;
  includeAddress: boolean;
};

export function storeProvideWalletAddress(src: ProvideWalletAddress) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(745978227, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeAddress(src.ownerAddress);
    b_0.storeBit(src.includeAddress);
  };
}

export function loadProvideWalletAddress(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 745978227) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _ownerAddress = sc_0.loadAddress();
  let _includeAddress = sc_0.loadBit();
  return {
    $$type: 'ProvideWalletAddress' as const,
    queryId: _queryId,
    ownerAddress: _ownerAddress,
    includeAddress: _includeAddress,
  };
}

function loadTupleProvideWalletAddress(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _ownerAddress = source.readAddress();
  let _includeAddress = source.readBoolean();
  return {
    $$type: 'ProvideWalletAddress' as const,
    queryId: _queryId,
    ownerAddress: _ownerAddress,
    includeAddress: _includeAddress,
  };
}

function loadGetterTupleProvideWalletAddress(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _ownerAddress = source.readAddress();
  let _includeAddress = source.readBoolean();
  return {
    $$type: 'ProvideWalletAddress' as const,
    queryId: _queryId,
    ownerAddress: _ownerAddress,
    includeAddress: _includeAddress,
  };
}

function storeTupleProvideWalletAddress(source: ProvideWalletAddress) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeAddress(source.ownerAddress);
  builder.writeBoolean(source.includeAddress);
  return builder.build();
}

function dictValueParserProvideWalletAddress(): DictionaryValue<ProvideWalletAddress> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeProvideWalletAddress(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadProvideWalletAddress(src.loadRef().beginParse());
    },
  };
}

export type TakeWalletAddress = {
  $$type: 'TakeWalletAddress';
  queryId: bigint;
  walletAddress: Address;
  ownerAddress: Address | null;
};

export function storeTakeWalletAddress(src: TakeWalletAddress) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(3513996288, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeAddress(src.walletAddress);
    b_0.storeAddress(src.ownerAddress);
  };
}

export function loadTakeWalletAddress(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 3513996288) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _walletAddress = sc_0.loadAddress();
  let _ownerAddress = sc_0.loadMaybeAddress();
  return {
    $$type: 'TakeWalletAddress' as const,
    queryId: _queryId,
    walletAddress: _walletAddress,
    ownerAddress: _ownerAddress,
  };
}

function loadTupleTakeWalletAddress(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _walletAddress = source.readAddress();
  let _ownerAddress = source.readAddressOpt();
  return {
    $$type: 'TakeWalletAddress' as const,
    queryId: _queryId,
    walletAddress: _walletAddress,
    ownerAddress: _ownerAddress,
  };
}

function loadGetterTupleTakeWalletAddress(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _walletAddress = source.readAddress();
  let _ownerAddress = source.readAddressOpt();
  return {
    $$type: 'TakeWalletAddress' as const,
    queryId: _queryId,
    walletAddress: _walletAddress,
    ownerAddress: _ownerAddress,
  };
}

function storeTupleTakeWalletAddress(source: TakeWalletAddress) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeAddress(source.walletAddress);
  builder.writeAddress(source.ownerAddress);
  return builder.build();
}

function dictValueParserTakeWalletAddress(): DictionaryValue<TakeWalletAddress> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeTakeWalletAddress(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadTakeWalletAddress(src.loadRef().beginParse());
    },
  };
}

export type UpgradeContract = {
  $$type: 'UpgradeContract';
  queryId: bigint;
  code: Cell | null;
  responseDestination: Address;
};

export function storeUpgradeContract(src: UpgradeContract) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(2785044657, 32);
    b_0.storeInt(src.queryId, 257);
    if (src.code !== null && src.code !== undefined) {
      b_0.storeBit(true).storeRef(src.code);
    } else {
      b_0.storeBit(false);
    }
    b_0.storeAddress(src.responseDestination);
  };
}

export function loadUpgradeContract(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 2785044657) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadIntBig(257);
  let _code = sc_0.loadBit() ? sc_0.loadRef() : null;
  let _responseDestination = sc_0.loadAddress();
  return {
    $$type: 'UpgradeContract' as const,
    queryId: _queryId,
    code: _code,
    responseDestination: _responseDestination,
  };
}

function loadTupleUpgradeContract(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _code = source.readCellOpt();
  let _responseDestination = source.readAddress();
  return {
    $$type: 'UpgradeContract' as const,
    queryId: _queryId,
    code: _code,
    responseDestination: _responseDestination,
  };
}

function loadGetterTupleUpgradeContract(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _code = source.readCellOpt();
  let _responseDestination = source.readAddress();
  return {
    $$type: 'UpgradeContract' as const,
    queryId: _queryId,
    code: _code,
    responseDestination: _responseDestination,
  };
}

function storeTupleUpgradeContract(source: UpgradeContract) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeCell(source.code);
  builder.writeAddress(source.responseDestination);
  return builder.build();
}

function dictValueParserUpgradeContract(): DictionaryValue<UpgradeContract> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeUpgradeContract(src)).endCell());
    },
    parse: (src) => {
      return loadUpgradeContract(src.loadRef().beginParse());
    },
  };
}

export type AdminCallTokenTransfer = {
  $$type: 'AdminCallTokenTransfer';
  queryId: bigint;
  amount: bigint;
  jettonContract: Address;
};

export function storeAdminCallTokenTransfer(src: AdminCallTokenTransfer) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(1185764112, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeInt(src.amount, 257);
    b_0.storeAddress(src.jettonContract);
  };
}

export function loadAdminCallTokenTransfer(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 1185764112) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _amount = sc_0.loadIntBig(257);
  let _jettonContract = sc_0.loadAddress();
  return {
    $$type: 'AdminCallTokenTransfer' as const,
    queryId: _queryId,
    amount: _amount,
    jettonContract: _jettonContract,
  };
}

function loadTupleAdminCallTokenTransfer(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _jettonContract = source.readAddress();
  return {
    $$type: 'AdminCallTokenTransfer' as const,
    queryId: _queryId,
    amount: _amount,
    jettonContract: _jettonContract,
  };
}

function loadGetterTupleAdminCallTokenTransfer(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _jettonContract = source.readAddress();
  return {
    $$type: 'AdminCallTokenTransfer' as const,
    queryId: _queryId,
    amount: _amount,
    jettonContract: _jettonContract,
  };
}

function storeTupleAdminCallTokenTransfer(source: AdminCallTokenTransfer) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeNumber(source.amount);
  builder.writeAddress(source.jettonContract);
  return builder.build();
}

function dictValueParserAdminCallTokenTransfer(): DictionaryValue<AdminCallTokenTransfer> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeAdminCallTokenTransfer(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadAdminCallTokenTransfer(src.loadRef().beginParse());
    },
  };
}

export type JettonWalletData = {
  $$type: 'JettonWalletData';
  balance: bigint;
  owner: Address;
  master: Address;
  walletCode: Cell;
};

export function storeJettonWalletData(src: JettonWalletData) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeInt(src.balance, 257);
    b_0.storeAddress(src.owner);
    b_0.storeAddress(src.master);
    b_0.storeRef(src.walletCode);
  };
}

export function loadJettonWalletData(slice: Slice) {
  let sc_0 = slice;
  let _balance = sc_0.loadIntBig(257);
  let _owner = sc_0.loadAddress();
  let _master = sc_0.loadAddress();
  let _walletCode = sc_0.loadRef();
  return {
    $$type: 'JettonWalletData' as const,
    balance: _balance,
    owner: _owner,
    master: _master,
    walletCode: _walletCode,
  };
}

function loadTupleJettonWalletData(source: TupleReader) {
  let _balance = source.readBigNumber();
  let _owner = source.readAddress();
  let _master = source.readAddress();
  let _walletCode = source.readCell();
  return {
    $$type: 'JettonWalletData' as const,
    balance: _balance,
    owner: _owner,
    master: _master,
    walletCode: _walletCode,
  };
}

function loadGetterTupleJettonWalletData(source: TupleReader) {
  let _balance = source.readBigNumber();
  let _owner = source.readAddress();
  let _master = source.readAddress();
  let _walletCode = source.readCell();
  return {
    $$type: 'JettonWalletData' as const,
    balance: _balance,
    owner: _owner,
    master: _master,
    walletCode: _walletCode,
  };
}

function storeTupleJettonWalletData(source: JettonWalletData) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.balance);
  builder.writeAddress(source.owner);
  builder.writeAddress(source.master);
  builder.writeCell(source.walletCode);
  return builder.build();
}

function dictValueParserJettonWalletData(): DictionaryValue<JettonWalletData> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeJettonWalletData(src)).endCell());
    },
    parse: (src) => {
      return loadJettonWalletData(src.loadRef().beginParse());
    },
  };
}

export type MintJetton = {
  $$type: 'MintJetton';
  queryId: bigint;
  amount: bigint;
  receiver: Address;
  sender: Address;
};

export function storeMintJetton(src: MintJetton) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(2928867052, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeInt(src.amount, 257);
    b_0.storeAddress(src.receiver);
    b_0.storeAddress(src.sender);
  };
}

export function loadMintJetton(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 2928867052) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _amount = sc_0.loadIntBig(257);
  let _receiver = sc_0.loadAddress();
  let _sender = sc_0.loadAddress();
  return {
    $$type: 'MintJetton' as const,
    queryId: _queryId,
    amount: _amount,
    receiver: _receiver,
    sender: _sender,
  };
}

function loadTupleMintJetton(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _receiver = source.readAddress();
  let _sender = source.readAddress();
  return {
    $$type: 'MintJetton' as const,
    queryId: _queryId,
    amount: _amount,
    receiver: _receiver,
    sender: _sender,
  };
}

function loadGetterTupleMintJetton(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _receiver = source.readAddress();
  let _sender = source.readAddress();
  return {
    $$type: 'MintJetton' as const,
    queryId: _queryId,
    amount: _amount,
    receiver: _receiver,
    sender: _sender,
  };
}

function storeTupleMintJetton(source: MintJetton) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeNumber(source.amount);
  builder.writeAddress(source.receiver);
  builder.writeAddress(source.sender);
  return builder.build();
}

function dictValueParserMintJetton(): DictionaryValue<MintJetton> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeMintJetton(src)).endCell());
    },
    parse: (src) => {
      return loadMintJetton(src.loadRef().beginParse());
    },
  };
}

export type MintJettonPublic = {
  $$type: 'MintJettonPublic';
  queryId: bigint;
  amount: bigint;
  sender: Address;
};

export function storeMintJettonPublic(src: MintJettonPublic) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(2570012896, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeInt(src.amount, 257);
    b_0.storeAddress(src.sender);
  };
}

export function loadMintJettonPublic(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 2570012896) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _amount = sc_0.loadIntBig(257);
  let _sender = sc_0.loadAddress();
  return {
    $$type: 'MintJettonPublic' as const,
    queryId: _queryId,
    amount: _amount,
    sender: _sender,
  };
}

function loadTupleMintJettonPublic(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _sender = source.readAddress();
  return {
    $$type: 'MintJettonPublic' as const,
    queryId: _queryId,
    amount: _amount,
    sender: _sender,
  };
}

function loadGetterTupleMintJettonPublic(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _sender = source.readAddress();
  return {
    $$type: 'MintJettonPublic' as const,
    queryId: _queryId,
    amount: _amount,
    sender: _sender,
  };
}

function storeTupleMintJettonPublic(source: MintJettonPublic) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeNumber(source.amount);
  builder.writeAddress(source.sender);
  return builder.build();
}

function dictValueParserMintJettonPublic(): DictionaryValue<MintJettonPublic> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeMintJettonPublic(src)).endCell());
    },
    parse: (src) => {
      return loadMintJettonPublic(src.loadRef().beginParse());
    },
  };
}

export type SellJettonPublic = {
  $$type: 'SellJettonPublic';
  queryId: bigint;
  amount: bigint;
  sender: Address;
};

export function storeSellJettonPublic(src: SellJettonPublic) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(3871468253, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeInt(src.amount, 257);
    b_0.storeAddress(src.sender);
  };
}

export function loadSellJettonPublic(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 3871468253) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _amount = sc_0.loadIntBig(257);
  let _sender = sc_0.loadAddress();
  return {
    $$type: 'SellJettonPublic' as const,
    queryId: _queryId,
    amount: _amount,
    sender: _sender,
  };
}

function loadTupleSellJettonPublic(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _sender = source.readAddress();
  return {
    $$type: 'SellJettonPublic' as const,
    queryId: _queryId,
    amount: _amount,
    sender: _sender,
  };
}

function loadGetterTupleSellJettonPublic(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _sender = source.readAddress();
  return {
    $$type: 'SellJettonPublic' as const,
    queryId: _queryId,
    amount: _amount,
    sender: _sender,
  };
}

function storeTupleSellJettonPublic(source: SellJettonPublic) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeNumber(source.amount);
  builder.writeAddress(source.sender);
  return builder.build();
}

function dictValueParserSellJettonPublic(): DictionaryValue<SellJettonPublic> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeSellJettonPublic(src)).endCell());
    },
    parse: (src) => {
      return loadSellJettonPublic(src.loadRef().beginParse());
    },
  };
}

export type ClaimJetton = {
  $$type: 'ClaimJetton';
  queryId: bigint;
  amount: bigint;
  sender: Address;
  jettonContractAddress: Address | null;
};

export function storeClaimJetton(src: ClaimJetton) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(2570012897, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeInt(src.amount, 257);
    b_0.storeAddress(src.sender);
    b_0.storeAddress(src.jettonContractAddress);
  };
}

export function loadClaimJetton(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 2570012897) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _amount = sc_0.loadIntBig(257);
  let _sender = sc_0.loadAddress();
  let _jettonContractAddress = sc_0.loadMaybeAddress();
  return {
    $$type: 'ClaimJetton' as const,
    queryId: _queryId,
    amount: _amount,
    sender: _sender,
    jettonContractAddress: _jettonContractAddress,
  };
}

function loadTupleClaimJetton(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _sender = source.readAddress();
  let _jettonContractAddress = source.readAddressOpt();
  return {
    $$type: 'ClaimJetton' as const,
    queryId: _queryId,
    amount: _amount,
    sender: _sender,
    jettonContractAddress: _jettonContractAddress,
  };
}

function loadGetterTupleClaimJetton(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _sender = source.readAddress();
  let _jettonContractAddress = source.readAddressOpt();
  return {
    $$type: 'ClaimJetton' as const,
    queryId: _queryId,
    amount: _amount,
    sender: _sender,
    jettonContractAddress: _jettonContractAddress,
  };
}

function storeTupleClaimJetton(source: ClaimJetton) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeNumber(source.amount);
  builder.writeAddress(source.sender);
  builder.writeAddress(source.jettonContractAddress);
  return builder.build();
}

function dictValueParserClaimJetton(): DictionaryValue<ClaimJetton> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeClaimJetton(src)).endCell());
    },
    parse: (src) => {
      return loadClaimJetton(src.loadRef().beginParse());
    },
  };
}

export type AdminCallTransferJetton = {
  $$type: 'AdminCallTransferJetton';
  queryId: bigint;
  nonce: Cell;
  signature: Slice;
  metadata: Cell;
  firstBuy: boolean | null;
};

export function storeAdminCallTransferJetton(src: AdminCallTransferJetton) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(260734632, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeRef(src.nonce);
    b_0.storeRef(src.signature.asCell());
    b_0.storeRef(src.metadata);
    if (src.firstBuy !== null && src.firstBuy !== undefined) {
      b_0.storeBit(true).storeBit(src.firstBuy);
    } else {
      b_0.storeBit(false);
    }
  };
}

export function loadAdminCallTransferJetton(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 260734632) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _nonce = sc_0.loadRef();
  let _signature = sc_0.loadRef().asSlice();
  let _metadata = sc_0.loadRef();
  let _firstBuy = sc_0.loadBit() ? sc_0.loadBit() : null;
  return {
    $$type: 'AdminCallTransferJetton' as const,
    queryId: _queryId,
    nonce: _nonce,
    signature: _signature,
    metadata: _metadata,
    firstBuy: _firstBuy,
  };
}

function loadTupleAdminCallTransferJetton(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _nonce = source.readCell();
  let _signature = source.readCell().asSlice();
  let _metadata = source.readCell();
  let _firstBuy = source.readBooleanOpt();
  return {
    $$type: 'AdminCallTransferJetton' as const,
    queryId: _queryId,
    nonce: _nonce,
    signature: _signature,
    metadata: _metadata,
    firstBuy: _firstBuy,
  };
}

function loadGetterTupleAdminCallTransferJetton(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _nonce = source.readCell();
  let _signature = source.readCell().asSlice();
  let _metadata = source.readCell();
  let _firstBuy = source.readBooleanOpt();
  return {
    $$type: 'AdminCallTransferJetton' as const,
    queryId: _queryId,
    nonce: _nonce,
    signature: _signature,
    metadata: _metadata,
    firstBuy: _firstBuy,
  };
}

function storeTupleAdminCallTransferJetton(source: AdminCallTransferJetton) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeCell(source.nonce);
  builder.writeSlice(source.signature.asCell());
  builder.writeCell(source.metadata);
  builder.writeBoolean(source.firstBuy);
  return builder.build();
}

function dictValueParserAdminCallTransferJetton(): DictionaryValue<AdminCallTransferJetton> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeAdminCallTransferJetton(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadAdminCallTransferJetton(src.loadRef().beginParse());
    },
  };
}

export type AdminCallClaimDexJetton = {
  $$type: 'AdminCallClaimDexJetton';
  queryId: bigint;
  nonce: Cell;
  signature: Slice;
  metadata: Cell;
  jettonContractAddress: Address;
};

export function storeAdminCallClaimDexJetton(src: AdminCallClaimDexJetton) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(260734631, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeRef(src.nonce);
    b_0.storeRef(src.signature.asCell());
    b_0.storeRef(src.metadata);
    b_0.storeAddress(src.jettonContractAddress);
  };
}

export function loadAdminCallClaimDexJetton(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 260734631) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _nonce = sc_0.loadRef();
  let _signature = sc_0.loadRef().asSlice();
  let _metadata = sc_0.loadRef();
  let _jettonContractAddress = sc_0.loadAddress();
  return {
    $$type: 'AdminCallClaimDexJetton' as const,
    queryId: _queryId,
    nonce: _nonce,
    signature: _signature,
    metadata: _metadata,
    jettonContractAddress: _jettonContractAddress,
  };
}

function loadTupleAdminCallClaimDexJetton(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _nonce = source.readCell();
  let _signature = source.readCell().asSlice();
  let _metadata = source.readCell();
  let _jettonContractAddress = source.readAddress();
  return {
    $$type: 'AdminCallClaimDexJetton' as const,
    queryId: _queryId,
    nonce: _nonce,
    signature: _signature,
    metadata: _metadata,
    jettonContractAddress: _jettonContractAddress,
  };
}

function loadGetterTupleAdminCallClaimDexJetton(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _nonce = source.readCell();
  let _signature = source.readCell().asSlice();
  let _metadata = source.readCell();
  let _jettonContractAddress = source.readAddress();
  return {
    $$type: 'AdminCallClaimDexJetton' as const,
    queryId: _queryId,
    nonce: _nonce,
    signature: _signature,
    metadata: _metadata,
    jettonContractAddress: _jettonContractAddress,
  };
}

function storeTupleAdminCallClaimDexJetton(source: AdminCallClaimDexJetton) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeCell(source.nonce);
  builder.writeSlice(source.signature.asCell());
  builder.writeCell(source.metadata);
  builder.writeAddress(source.jettonContractAddress);
  return builder.build();
}

function dictValueParserAdminCallClaimDexJetton(): DictionaryValue<AdminCallClaimDexJetton> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeAdminCallClaimDexJetton(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadAdminCallClaimDexJetton(src.loadRef().beginParse());
    },
  };
}

export type AdminCallDailyClaimDexJetton = {
  $$type: 'AdminCallDailyClaimDexJetton';
  queryId: bigint;
  nonce: Cell;
  signature: Slice;
  metadata: Cell;
};

export function storeAdminCallDailyClaimDexJetton(
  src: AdminCallDailyClaimDexJetton,
) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(260734630, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeRef(src.nonce);
    b_0.storeRef(src.signature.asCell());
    b_0.storeRef(src.metadata);
  };
}

export function loadAdminCallDailyClaimDexJetton(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 260734630) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _nonce = sc_0.loadRef();
  let _signature = sc_0.loadRef().asSlice();
  let _metadata = sc_0.loadRef();
  return {
    $$type: 'AdminCallDailyClaimDexJetton' as const,
    queryId: _queryId,
    nonce: _nonce,
    signature: _signature,
    metadata: _metadata,
  };
}

function loadTupleAdminCallDailyClaimDexJetton(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _nonce = source.readCell();
  let _signature = source.readCell().asSlice();
  let _metadata = source.readCell();
  return {
    $$type: 'AdminCallDailyClaimDexJetton' as const,
    queryId: _queryId,
    nonce: _nonce,
    signature: _signature,
    metadata: _metadata,
  };
}

function loadGetterTupleAdminCallDailyClaimDexJetton(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _nonce = source.readCell();
  let _signature = source.readCell().asSlice();
  let _metadata = source.readCell();
  return {
    $$type: 'AdminCallDailyClaimDexJetton' as const,
    queryId: _queryId,
    nonce: _nonce,
    signature: _signature,
    metadata: _metadata,
  };
}

function storeTupleAdminCallDailyClaimDexJetton(
  source: AdminCallDailyClaimDexJetton,
) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeCell(source.nonce);
  builder.writeSlice(source.signature.asCell());
  builder.writeCell(source.metadata);
  return builder.build();
}

function dictValueParserAdminCallDailyClaimDexJetton(): DictionaryValue<AdminCallDailyClaimDexJetton> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeAdminCallDailyClaimDexJetton(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadAdminCallDailyClaimDexJetton(src.loadRef().beginParse());
    },
  };
}

export type AdminCallSellJetton = {
  $$type: 'AdminCallSellJetton';
  queryId: bigint;
  nonce: Cell;
  signature: Slice;
  metadata: Cell;
};

export function storeAdminCallSellJetton(src: AdminCallSellJetton) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(260734633, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeRef(src.nonce);
    b_0.storeRef(src.signature.asCell());
    b_0.storeRef(src.metadata);
  };
}

export function loadAdminCallSellJetton(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 260734633) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _nonce = sc_0.loadRef();
  let _signature = sc_0.loadRef().asSlice();
  let _metadata = sc_0.loadRef();
  return {
    $$type: 'AdminCallSellJetton' as const,
    queryId: _queryId,
    nonce: _nonce,
    signature: _signature,
    metadata: _metadata,
  };
}

function loadTupleAdminCallSellJetton(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _nonce = source.readCell();
  let _signature = source.readCell().asSlice();
  let _metadata = source.readCell();
  return {
    $$type: 'AdminCallSellJetton' as const,
    queryId: _queryId,
    nonce: _nonce,
    signature: _signature,
    metadata: _metadata,
  };
}

function loadGetterTupleAdminCallSellJetton(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _nonce = source.readCell();
  let _signature = source.readCell().asSlice();
  let _metadata = source.readCell();
  return {
    $$type: 'AdminCallSellJetton' as const,
    queryId: _queryId,
    nonce: _nonce,
    signature: _signature,
    metadata: _metadata,
  };
}

function storeTupleAdminCallSellJetton(source: AdminCallSellJetton) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeCell(source.nonce);
  builder.writeSlice(source.signature.asCell());
  builder.writeCell(source.metadata);
  return builder.build();
}

function dictValueParserAdminCallSellJetton(): DictionaryValue<AdminCallSellJetton> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeAdminCallSellJetton(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadAdminCallSellJetton(src.loadRef().beginParse());
    },
  };
}

export type TokenUpdateContent = {
  $$type: 'TokenUpdateContent';
  content: Cell;
};

export function storeTokenUpdateContent(src: TokenUpdateContent) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(439303245, 32);
    b_0.storeRef(src.content);
  };
}

export function loadTokenUpdateContent(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 439303245) {
    throw Error('Invalid prefix');
  }
  let _content = sc_0.loadRef();
  return { $$type: 'TokenUpdateContent' as const, content: _content };
}

function loadTupleTokenUpdateContent(source: TupleReader) {
  let _content = source.readCell();
  return { $$type: 'TokenUpdateContent' as const, content: _content };
}

function loadGetterTupleTokenUpdateContent(source: TupleReader) {
  let _content = source.readCell();
  return { $$type: 'TokenUpdateContent' as const, content: _content };
}

function storeTupleTokenUpdateContent(source: TokenUpdateContent) {
  let builder = new TupleBuilder();
  builder.writeCell(source.content);
  return builder.build();
}

function dictValueParserTokenUpdateContent(): DictionaryValue<TokenUpdateContent> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeTokenUpdateContent(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadTokenUpdateContent(src.loadRef().beginParse());
    },
  };
}

export type UpdateAdministrator = {
  $$type: 'UpdateAdministrator';
  queryId: bigint;
  newAdministrator: Address;
};

export function storeUpdateAdministrator(src: UpdateAdministrator) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(1533902479, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeAddress(src.newAdministrator);
  };
}

export function loadUpdateAdministrator(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 1533902479) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _newAdministrator = sc_0.loadAddress();
  return {
    $$type: 'UpdateAdministrator' as const,
    queryId: _queryId,
    newAdministrator: _newAdministrator,
  };
}

function loadTupleUpdateAdministrator(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _newAdministrator = source.readAddress();
  return {
    $$type: 'UpdateAdministrator' as const,
    queryId: _queryId,
    newAdministrator: _newAdministrator,
  };
}

function loadGetterTupleUpdateAdministrator(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _newAdministrator = source.readAddress();
  return {
    $$type: 'UpdateAdministrator' as const,
    queryId: _queryId,
    newAdministrator: _newAdministrator,
  };
}

function storeTupleUpdateAdministrator(source: UpdateAdministrator) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeAddress(source.newAdministrator);
  return builder.build();
}

function dictValueParserUpdateAdministrator(): DictionaryValue<UpdateAdministrator> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeUpdateAdministrator(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadUpdateAdministrator(src.loadRef().beginParse());
    },
  };
}

export type UpdatePublicKey = {
  $$type: 'UpdatePublicKey';
  queryId: bigint;
  publicKey: bigint;
};

export function storeUpdatePublicKey(src: UpdatePublicKey) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(2618105391, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeUint(src.publicKey, 256);
  };
}

export function loadUpdatePublicKey(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 2618105391) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _publicKey = sc_0.loadUintBig(256);
  return {
    $$type: 'UpdatePublicKey' as const,
    queryId: _queryId,
    publicKey: _publicKey,
  };
}

function loadTupleUpdatePublicKey(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _publicKey = source.readBigNumber();
  return {
    $$type: 'UpdatePublicKey' as const,
    queryId: _queryId,
    publicKey: _publicKey,
  };
}

function loadGetterTupleUpdatePublicKey(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _publicKey = source.readBigNumber();
  return {
    $$type: 'UpdatePublicKey' as const,
    queryId: _queryId,
    publicKey: _publicKey,
  };
}

function storeTupleUpdatePublicKey(source: UpdatePublicKey) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeNumber(source.publicKey);
  return builder.build();
}

function dictValueParserUpdatePublicKey(): DictionaryValue<UpdatePublicKey> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeUpdatePublicKey(src)).endCell());
    },
    parse: (src) => {
      return loadUpdatePublicKey(src.loadRef().beginParse());
    },
  };
}

export type UpdateTxFee = {
  $$type: 'UpdateTxFee';
  queryId: bigint;
  txFee: bigint;
};

export function storeUpdateTxFee(src: UpdateTxFee) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(1062099563, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeInt(src.txFee, 257);
  };
}

export function loadUpdateTxFee(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 1062099563) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _txFee = sc_0.loadIntBig(257);
  return { $$type: 'UpdateTxFee' as const, queryId: _queryId, txFee: _txFee };
}

function loadTupleUpdateTxFee(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _txFee = source.readBigNumber();
  return { $$type: 'UpdateTxFee' as const, queryId: _queryId, txFee: _txFee };
}

function loadGetterTupleUpdateTxFee(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _txFee = source.readBigNumber();
  return { $$type: 'UpdateTxFee' as const, queryId: _queryId, txFee: _txFee };
}

function storeTupleUpdateTxFee(source: UpdateTxFee) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeNumber(source.txFee);
  return builder.build();
}

function dictValueParserUpdateTxFee(): DictionaryValue<UpdateTxFee> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeUpdateTxFee(src)).endCell());
    },
    parse: (src) => {
      return loadUpdateTxFee(src.loadRef().beginParse());
    },
  };
}

export type UpdateCurveConfig = {
  $$type: 'UpdateCurveConfig';
  queryId: bigint;
  curveConfig: CurveConfig;
};

export function storeUpdateCurveConfig(src: UpdateCurveConfig) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(2106498826, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.store(storeCurveConfig(src.curveConfig));
  };
}

export function loadUpdateCurveConfig(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 2106498826) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _curveConfig = loadCurveConfig(sc_0);
  return {
    $$type: 'UpdateCurveConfig' as const,
    queryId: _queryId,
    curveConfig: _curveConfig,
  };
}

function loadTupleUpdateCurveConfig(source: TupleReader) {
  let _queryId = source.readBigNumber();
  const _curveConfig = loadTupleCurveConfig(source);
  return {
    $$type: 'UpdateCurveConfig' as const,
    queryId: _queryId,
    curveConfig: _curveConfig,
  };
}

function loadGetterTupleUpdateCurveConfig(source: TupleReader) {
  let _queryId = source.readBigNumber();
  const _curveConfig = loadGetterTupleCurveConfig(source);
  return {
    $$type: 'UpdateCurveConfig' as const,
    queryId: _queryId,
    curveConfig: _curveConfig,
  };
}

function storeTupleUpdateCurveConfig(source: UpdateCurveConfig) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeTuple(storeTupleCurveConfig(source.curveConfig));
  return builder.build();
}

function dictValueParserUpdateCurveConfig(): DictionaryValue<UpdateCurveConfig> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeUpdateCurveConfig(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadUpdateCurveConfig(src.loadRef().beginParse());
    },
  };
}

export type UpdateDexJettonContractAddress = {
  $$type: 'UpdateDexJettonContractAddress';
  queryId: bigint;
  dexJettonContractAddress: Address;
};

export function storeUpdateDexJettonContractAddress(
  src: UpdateDexJettonContractAddress,
) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(1248541741, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeAddress(src.dexJettonContractAddress);
  };
}

export function loadUpdateDexJettonContractAddress(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 1248541741) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _dexJettonContractAddress = sc_0.loadAddress();
  return {
    $$type: 'UpdateDexJettonContractAddress' as const,
    queryId: _queryId,
    dexJettonContractAddress: _dexJettonContractAddress,
  };
}

function loadTupleUpdateDexJettonContractAddress(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _dexJettonContractAddress = source.readAddress();
  return {
    $$type: 'UpdateDexJettonContractAddress' as const,
    queryId: _queryId,
    dexJettonContractAddress: _dexJettonContractAddress,
  };
}

function loadGetterTupleUpdateDexJettonContractAddress(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _dexJettonContractAddress = source.readAddress();
  return {
    $$type: 'UpdateDexJettonContractAddress' as const,
    queryId: _queryId,
    dexJettonContractAddress: _dexJettonContractAddress,
  };
}

function storeTupleUpdateDexJettonContractAddress(
  source: UpdateDexJettonContractAddress,
) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeAddress(source.dexJettonContractAddress);
  return builder.build();
}

function dictValueParserUpdateDexJettonContractAddress(): DictionaryValue<UpdateDexJettonContractAddress> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeUpdateDexJettonContractAddress(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadUpdateDexJettonContractAddress(src.loadRef().beginParse());
    },
  };
}

export type JettonDeploy = {
  $$type: 'JettonDeploy';
  queryId: bigint;
  metadata: Cell;
  nonce: Cell;
  signature: Slice;
};

export function storeJettonDeploy(src: JettonDeploy) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(3035222952, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeRef(src.metadata);
    b_0.storeRef(src.nonce);
    b_0.storeRef(src.signature.asCell());
  };
}

export function loadJettonDeploy(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 3035222952) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _metadata = sc_0.loadRef();
  let _nonce = sc_0.loadRef();
  let _signature = sc_0.loadRef().asSlice();
  return {
    $$type: 'JettonDeploy' as const,
    queryId: _queryId,
    metadata: _metadata,
    nonce: _nonce,
    signature: _signature,
  };
}

function loadTupleJettonDeploy(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _metadata = source.readCell();
  let _nonce = source.readCell();
  let _signature = source.readCell().asSlice();
  return {
    $$type: 'JettonDeploy' as const,
    queryId: _queryId,
    metadata: _metadata,
    nonce: _nonce,
    signature: _signature,
  };
}

function loadGetterTupleJettonDeploy(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _metadata = source.readCell();
  let _nonce = source.readCell();
  let _signature = source.readCell().asSlice();
  return {
    $$type: 'JettonDeploy' as const,
    queryId: _queryId,
    metadata: _metadata,
    nonce: _nonce,
    signature: _signature,
  };
}

function storeTupleJettonDeploy(source: JettonDeploy) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeCell(source.metadata);
  builder.writeCell(source.nonce);
  builder.writeSlice(source.signature.asCell());
  return builder.build();
}

function dictValueParserJettonDeploy(): DictionaryValue<JettonDeploy> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeJettonDeploy(src)).endCell());
    },
    parse: (src) => {
      return loadJettonDeploy(src.loadRef().beginParse());
    },
  };
}

export type JettonDexDeploy = {
  $$type: 'JettonDexDeploy';
  queryId: bigint;
  metadata: Cell;
  nonce: Cell;
  signature: Slice;
};

export function storeJettonDexDeploy(src: JettonDexDeploy) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(2750923441, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeRef(src.metadata);
    b_0.storeRef(src.nonce);
    b_0.storeRef(src.signature.asCell());
  };
}

export function loadJettonDexDeploy(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 2750923441) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _metadata = sc_0.loadRef();
  let _nonce = sc_0.loadRef();
  let _signature = sc_0.loadRef().asSlice();
  return {
    $$type: 'JettonDexDeploy' as const,
    queryId: _queryId,
    metadata: _metadata,
    nonce: _nonce,
    signature: _signature,
  };
}

function loadTupleJettonDexDeploy(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _metadata = source.readCell();
  let _nonce = source.readCell();
  let _signature = source.readCell().asSlice();
  return {
    $$type: 'JettonDexDeploy' as const,
    queryId: _queryId,
    metadata: _metadata,
    nonce: _nonce,
    signature: _signature,
  };
}

function loadGetterTupleJettonDexDeploy(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _metadata = source.readCell();
  let _nonce = source.readCell();
  let _signature = source.readCell().asSlice();
  return {
    $$type: 'JettonDexDeploy' as const,
    queryId: _queryId,
    metadata: _metadata,
    nonce: _nonce,
    signature: _signature,
  };
}

function storeTupleJettonDexDeploy(source: JettonDexDeploy) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeCell(source.metadata);
  builder.writeCell(source.nonce);
  builder.writeSlice(source.signature.asCell());
  return builder.build();
}

function dictValueParserJettonDexDeploy(): DictionaryValue<JettonDexDeploy> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeJettonDexDeploy(src)).endCell());
    },
    parse: (src) => {
      return loadJettonDexDeploy(src.loadRef().beginParse());
    },
  };
}

export type JettonDeployWithCode = {
  $$type: 'JettonDeployWithCode';
  queryId: bigint;
  owner: Address;
  code: Cell;
  data: Cell;
  jettonContractAddress: Address;
  signature: Slice;
  offchainId: bigint;
};

export function storeJettonDeployWithCode(src: JettonDeployWithCode) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(4152114271, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeAddress(src.owner);
    b_0.storeRef(src.code);
    b_0.storeRef(src.data);
    b_0.storeAddress(src.jettonContractAddress);
    b_0.storeRef(src.signature.asCell());
    b_0.storeInt(src.offchainId, 257);
  };
}

export function loadJettonDeployWithCode(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 4152114271) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _owner = sc_0.loadAddress();
  let _code = sc_0.loadRef();
  let _data = sc_0.loadRef();
  let _jettonContractAddress = sc_0.loadAddress();
  let _signature = sc_0.loadRef().asSlice();
  let _offchainId = sc_0.loadIntBig(257);
  return {
    $$type: 'JettonDeployWithCode' as const,
    queryId: _queryId,
    owner: _owner,
    code: _code,
    data: _data,
    jettonContractAddress: _jettonContractAddress,
    signature: _signature,
    offchainId: _offchainId,
  };
}

function loadTupleJettonDeployWithCode(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _owner = source.readAddress();
  let _code = source.readCell();
  let _data = source.readCell();
  let _jettonContractAddress = source.readAddress();
  let _signature = source.readCell().asSlice();
  let _offchainId = source.readBigNumber();
  return {
    $$type: 'JettonDeployWithCode' as const,
    queryId: _queryId,
    owner: _owner,
    code: _code,
    data: _data,
    jettonContractAddress: _jettonContractAddress,
    signature: _signature,
    offchainId: _offchainId,
  };
}

function loadGetterTupleJettonDeployWithCode(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _owner = source.readAddress();
  let _code = source.readCell();
  let _data = source.readCell();
  let _jettonContractAddress = source.readAddress();
  let _signature = source.readCell().asSlice();
  let _offchainId = source.readBigNumber();
  return {
    $$type: 'JettonDeployWithCode' as const,
    queryId: _queryId,
    owner: _owner,
    code: _code,
    data: _data,
    jettonContractAddress: _jettonContractAddress,
    signature: _signature,
    offchainId: _offchainId,
  };
}

function storeTupleJettonDeployWithCode(source: JettonDeployWithCode) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeAddress(source.owner);
  builder.writeCell(source.code);
  builder.writeCell(source.data);
  builder.writeAddress(source.jettonContractAddress);
  builder.writeSlice(source.signature.asCell());
  builder.writeNumber(source.offchainId);
  return builder.build();
}

function dictValueParserJettonDeployWithCode(): DictionaryValue<JettonDeployWithCode> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeJettonDeployWithCode(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadJettonDeployWithCode(src.loadRef().beginParse());
    },
  };
}

export type VerifyDataSignature = {
  $$type: 'VerifyDataSignature';
  queryId: bigint;
  data: Cell;
  signature: Slice;
};

export function storeVerifyDataSignature(src: VerifyDataSignature) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(2997937648, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeRef(src.data);
    b_0.storeRef(src.signature.asCell());
  };
}

export function loadVerifyDataSignature(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 2997937648) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _data = sc_0.loadRef();
  let _signature = sc_0.loadRef().asSlice();
  return {
    $$type: 'VerifyDataSignature' as const,
    queryId: _queryId,
    data: _data,
    signature: _signature,
  };
}

function loadTupleVerifyDataSignature(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _data = source.readCell();
  let _signature = source.readCell().asSlice();
  return {
    $$type: 'VerifyDataSignature' as const,
    queryId: _queryId,
    data: _data,
    signature: _signature,
  };
}

function loadGetterTupleVerifyDataSignature(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _data = source.readCell();
  let _signature = source.readCell().asSlice();
  return {
    $$type: 'VerifyDataSignature' as const,
    queryId: _queryId,
    data: _data,
    signature: _signature,
  };
}

function storeTupleVerifyDataSignature(source: VerifyDataSignature) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeCell(source.data);
  builder.writeSlice(source.signature.asCell());
  return builder.build();
}

function dictValueParserVerifyDataSignature(): DictionaryValue<VerifyDataSignature> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeVerifyDataSignature(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadVerifyDataSignature(src.loadRef().beginParse());
    },
  };
}

export type Ping = {
  $$type: 'Ping';
  ping: string;
};

export function storePing(src: Ping) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(1267924010, 32);
    b_0.storeStringRefTail(src.ping);
  };
}

export function loadPing(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 1267924010) {
    throw Error('Invalid prefix');
  }
  let _ping = sc_0.loadStringRefTail();
  return { $$type: 'Ping' as const, ping: _ping };
}

function loadTuplePing(source: TupleReader) {
  let _ping = source.readString();
  return { $$type: 'Ping' as const, ping: _ping };
}

function loadGetterTuplePing(source: TupleReader) {
  let _ping = source.readString();
  return { $$type: 'Ping' as const, ping: _ping };
}

function storeTuplePing(source: Ping) {
  let builder = new TupleBuilder();
  builder.writeString(source.ping);
  return builder.build();
}

function dictValueParserPing(): DictionaryValue<Ping> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storePing(src)).endCell());
    },
    parse: (src) => {
      return loadPing(src.loadRef().beginParse());
    },
  };
}

export type TransferClaimedJetton = {
  $$type: 'TransferClaimedJetton';
  queryId: bigint;
  amount: bigint;
  jettonOwner: Address;
  jettonContractAddress: Address;
};

export function storeTransferClaimedJetton(src: TransferClaimedJetton) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(384998329, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeInt(src.amount, 257);
    b_0.storeAddress(src.jettonOwner);
    b_0.storeAddress(src.jettonContractAddress);
  };
}

export function loadTransferClaimedJetton(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 384998329) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _amount = sc_0.loadIntBig(257);
  let _jettonOwner = sc_0.loadAddress();
  let _jettonContractAddress = sc_0.loadAddress();
  return {
    $$type: 'TransferClaimedJetton' as const,
    queryId: _queryId,
    amount: _amount,
    jettonOwner: _jettonOwner,
    jettonContractAddress: _jettonContractAddress,
  };
}

function loadTupleTransferClaimedJetton(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _jettonOwner = source.readAddress();
  let _jettonContractAddress = source.readAddress();
  return {
    $$type: 'TransferClaimedJetton' as const,
    queryId: _queryId,
    amount: _amount,
    jettonOwner: _jettonOwner,
    jettonContractAddress: _jettonContractAddress,
  };
}

function loadGetterTupleTransferClaimedJetton(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _jettonOwner = source.readAddress();
  let _jettonContractAddress = source.readAddress();
  return {
    $$type: 'TransferClaimedJetton' as const,
    queryId: _queryId,
    amount: _amount,
    jettonOwner: _jettonOwner,
    jettonContractAddress: _jettonContractAddress,
  };
}

function storeTupleTransferClaimedJetton(source: TransferClaimedJetton) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeNumber(source.amount);
  builder.writeAddress(source.jettonOwner);
  builder.writeAddress(source.jettonContractAddress);
  return builder.build();
}

function dictValueParserTransferClaimedJetton(): DictionaryValue<TransferClaimedJetton> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeTransferClaimedJetton(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadTransferClaimedJetton(src.loadRef().beginParse());
    },
  };
}

export type TokenClaimNotification = {
  $$type: 'TokenClaimNotification';
  queryId: bigint;
  amount: bigint;
  jettonOwner: Address;
  jettonContractAddress: Address;
};

export function storeTokenClaimNotification(src: TokenClaimNotification) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(1430958165, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeInt(src.amount, 257);
    b_0.storeAddress(src.jettonOwner);
    b_0.storeAddress(src.jettonContractAddress);
  };
}

export function loadTokenClaimNotification(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 1430958165) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _amount = sc_0.loadIntBig(257);
  let _jettonOwner = sc_0.loadAddress();
  let _jettonContractAddress = sc_0.loadAddress();
  return {
    $$type: 'TokenClaimNotification' as const,
    queryId: _queryId,
    amount: _amount,
    jettonOwner: _jettonOwner,
    jettonContractAddress: _jettonContractAddress,
  };
}

function loadTupleTokenClaimNotification(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _jettonOwner = source.readAddress();
  let _jettonContractAddress = source.readAddress();
  return {
    $$type: 'TokenClaimNotification' as const,
    queryId: _queryId,
    amount: _amount,
    jettonOwner: _jettonOwner,
    jettonContractAddress: _jettonContractAddress,
  };
}

function loadGetterTupleTokenClaimNotification(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _jettonOwner = source.readAddress();
  let _jettonContractAddress = source.readAddress();
  return {
    $$type: 'TokenClaimNotification' as const,
    queryId: _queryId,
    amount: _amount,
    jettonOwner: _jettonOwner,
    jettonContractAddress: _jettonContractAddress,
  };
}

function storeTupleTokenClaimNotification(source: TokenClaimNotification) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeNumber(source.amount);
  builder.writeAddress(source.jettonOwner);
  builder.writeAddress(source.jettonContractAddress);
  return builder.build();
}

function dictValueParserTokenClaimNotification(): DictionaryValue<TokenClaimNotification> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeTokenClaimNotification(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadTokenClaimNotification(src.loadRef().beginParse());
    },
  };
}

export type CurveConfig = {
  $$type: 'CurveConfig';
  maxSupply: bigint;
  maxTargetPrice: bigint;
  initialPrice: bigint;
};

export function storeCurveConfig(src: CurveConfig) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeInt(src.maxSupply, 257);
    b_0.storeInt(src.maxTargetPrice, 257);
    b_0.storeInt(src.initialPrice, 257);
  };
}

export function loadCurveConfig(slice: Slice) {
  let sc_0 = slice;
  let _maxSupply = sc_0.loadIntBig(257);
  let _maxTargetPrice = sc_0.loadIntBig(257);
  let _initialPrice = sc_0.loadIntBig(257);
  return {
    $$type: 'CurveConfig' as const,
    maxSupply: _maxSupply,
    maxTargetPrice: _maxTargetPrice,
    initialPrice: _initialPrice,
  };
}

function loadTupleCurveConfig(source: TupleReader) {
  let _maxSupply = source.readBigNumber();
  let _maxTargetPrice = source.readBigNumber();
  let _initialPrice = source.readBigNumber();
  return {
    $$type: 'CurveConfig' as const,
    maxSupply: _maxSupply,
    maxTargetPrice: _maxTargetPrice,
    initialPrice: _initialPrice,
  };
}

function loadGetterTupleCurveConfig(source: TupleReader) {
  let _maxSupply = source.readBigNumber();
  let _maxTargetPrice = source.readBigNumber();
  let _initialPrice = source.readBigNumber();
  return {
    $$type: 'CurveConfig' as const,
    maxSupply: _maxSupply,
    maxTargetPrice: _maxTargetPrice,
    initialPrice: _initialPrice,
  };
}

function storeTupleCurveConfig(source: CurveConfig) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.maxSupply);
  builder.writeNumber(source.maxTargetPrice);
  builder.writeNumber(source.initialPrice);
  return builder.build();
}

function dictValueParserCurveConfig(): DictionaryValue<CurveConfig> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeCurveConfig(src)).endCell());
    },
    parse: (src) => {
      return loadCurveConfig(src.loadRef().beginParse());
    },
  };
}

export type JettonContractConfig = {
  $$type: 'JettonContractConfig';
  curveConfig: CurveConfig;
  administrator: Address;
  txFee: bigint;
};

export function storeJettonContractConfig(src: JettonContractConfig) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.store(storeCurveConfig(src.curveConfig));
    let b_1 = new Builder();
    b_1.storeAddress(src.administrator);
    b_1.storeInt(src.txFee, 257);
    b_0.storeRef(b_1.endCell());
  };
}

export function loadJettonContractConfig(slice: Slice) {
  let sc_0 = slice;
  let _curveConfig = loadCurveConfig(sc_0);
  let sc_1 = sc_0.loadRef().beginParse();
  let _administrator = sc_1.loadAddress();
  let _txFee = sc_1.loadIntBig(257);
  return {
    $$type: 'JettonContractConfig' as const,
    curveConfig: _curveConfig,
    administrator: _administrator,
    txFee: _txFee,
  };
}

function loadTupleJettonContractConfig(source: TupleReader) {
  const _curveConfig = loadTupleCurveConfig(source);
  let _administrator = source.readAddress();
  let _txFee = source.readBigNumber();
  return {
    $$type: 'JettonContractConfig' as const,
    curveConfig: _curveConfig,
    administrator: _administrator,
    txFee: _txFee,
  };
}

function loadGetterTupleJettonContractConfig(source: TupleReader) {
  const _curveConfig = loadGetterTupleCurveConfig(source);
  let _administrator = source.readAddress();
  let _txFee = source.readBigNumber();
  return {
    $$type: 'JettonContractConfig' as const,
    curveConfig: _curveConfig,
    administrator: _administrator,
    txFee: _txFee,
  };
}

function storeTupleJettonContractConfig(source: JettonContractConfig) {
  let builder = new TupleBuilder();
  builder.writeTuple(storeTupleCurveConfig(source.curveConfig));
  builder.writeAddress(source.administrator);
  builder.writeNumber(source.txFee);
  return builder.build();
}

function dictValueParserJettonContractConfig(): DictionaryValue<JettonContractConfig> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeJettonContractConfig(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadJettonContractConfig(src.loadRef().beginParse());
    },
  };
}

export type JettonDexContractConfig = {
  $$type: 'JettonDexContractConfig';
  maxSupply: bigint;
  administrator: Address;
};

export function storeJettonDexContractConfig(src: JettonDexContractConfig) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeInt(src.maxSupply, 257);
    b_0.storeAddress(src.administrator);
  };
}

export function loadJettonDexContractConfig(slice: Slice) {
  let sc_0 = slice;
  let _maxSupply = sc_0.loadIntBig(257);
  let _administrator = sc_0.loadAddress();
  return {
    $$type: 'JettonDexContractConfig' as const,
    maxSupply: _maxSupply,
    administrator: _administrator,
  };
}

function loadTupleJettonDexContractConfig(source: TupleReader) {
  let _maxSupply = source.readBigNumber();
  let _administrator = source.readAddress();
  return {
    $$type: 'JettonDexContractConfig' as const,
    maxSupply: _maxSupply,
    administrator: _administrator,
  };
}

function loadGetterTupleJettonDexContractConfig(source: TupleReader) {
  let _maxSupply = source.readBigNumber();
  let _administrator = source.readAddress();
  return {
    $$type: 'JettonDexContractConfig' as const,
    maxSupply: _maxSupply,
    administrator: _administrator,
  };
}

function storeTupleJettonDexContractConfig(source: JettonDexContractConfig) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.maxSupply);
  builder.writeAddress(source.administrator);
  return builder.build();
}

function dictValueParserJettonDexContractConfig(): DictionaryValue<JettonDexContractConfig> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeJettonDexContractConfig(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadJettonDexContractConfig(src.loadRef().beginParse());
    },
  };
}

export type MintAmount = {
  $$type: 'MintAmount';
  amount: bigint;
};

export function storeMintAmount(src: MintAmount) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeInt(src.amount, 257);
  };
}

export function loadMintAmount(slice: Slice) {
  let sc_0 = slice;
  let _amount = sc_0.loadIntBig(257);
  return { $$type: 'MintAmount' as const, amount: _amount };
}

function loadTupleMintAmount(source: TupleReader) {
  let _amount = source.readBigNumber();
  return { $$type: 'MintAmount' as const, amount: _amount };
}

function loadGetterTupleMintAmount(source: TupleReader) {
  let _amount = source.readBigNumber();
  return { $$type: 'MintAmount' as const, amount: _amount };
}

function storeTupleMintAmount(source: MintAmount) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.amount);
  return builder.build();
}

function dictValueParserMintAmount(): DictionaryValue<MintAmount> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeMintAmount(src)).endCell());
    },
    parse: (src) => {
      return loadMintAmount(src.loadRef().beginParse());
    },
  };
}

export type GemzJetton$Data = {
  $$type: 'GemzJetton$Data';
  totalSupply: bigint;
  owner: Address;
  administrator: Address;
  content: Cell;
  mintable: boolean;
  max_supply: bigint;
  curveConfig: CurveConfig;
  txFee: bigint;
  forward_ton_amount: bigint;
  dexJettonContractAddress: Address | null;
  burnAddress: Address;
};

export function storeGemzJetton$Data(src: GemzJetton$Data) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeCoins(src.totalSupply);
    b_0.storeAddress(src.owner);
    b_0.storeAddress(src.administrator);
    b_0.storeRef(src.content);
    b_0.storeBit(src.mintable);
    b_0.storeCoins(src.max_supply);
    let b_1 = new Builder();
    b_1.store(storeCurveConfig(src.curveConfig));
    let b_2 = new Builder();
    b_2.storeInt(src.txFee, 257);
    b_2.storeInt(src.forward_ton_amount, 257);
    b_2.storeAddress(src.dexJettonContractAddress);
    let b_3 = new Builder();
    b_3.storeAddress(src.burnAddress);
    b_2.storeRef(b_3.endCell());
    b_1.storeRef(b_2.endCell());
    b_0.storeRef(b_1.endCell());
  };
}

export function loadGemzJetton$Data(slice: Slice) {
  let sc_0 = slice;
  let _totalSupply = sc_0.loadCoins();
  let _owner = sc_0.loadAddress();
  let _administrator = sc_0.loadAddress();
  let _content = sc_0.loadRef();
  let _mintable = sc_0.loadBit();
  let _max_supply = sc_0.loadCoins();
  let sc_1 = sc_0.loadRef().beginParse();
  let _curveConfig = loadCurveConfig(sc_1);
  let sc_2 = sc_1.loadRef().beginParse();
  let _txFee = sc_2.loadIntBig(257);
  let _forward_ton_amount = sc_2.loadIntBig(257);
  let _dexJettonContractAddress = sc_2.loadMaybeAddress();
  let sc_3 = sc_2.loadRef().beginParse();
  let _burnAddress = sc_3.loadAddress();
  return {
    $$type: 'GemzJetton$Data' as const,
    totalSupply: _totalSupply,
    owner: _owner,
    administrator: _administrator,
    content: _content,
    mintable: _mintable,
    max_supply: _max_supply,
    curveConfig: _curveConfig,
    txFee: _txFee,
    forward_ton_amount: _forward_ton_amount,
    dexJettonContractAddress: _dexJettonContractAddress,
    burnAddress: _burnAddress,
  };
}

function loadTupleGemzJetton$Data(source: TupleReader) {
  let _totalSupply = source.readBigNumber();
  let _owner = source.readAddress();
  let _administrator = source.readAddress();
  let _content = source.readCell();
  let _mintable = source.readBoolean();
  let _max_supply = source.readBigNumber();
  const _curveConfig = loadTupleCurveConfig(source);
  let _txFee = source.readBigNumber();
  let _forward_ton_amount = source.readBigNumber();
  let _dexJettonContractAddress = source.readAddressOpt();
  let _burnAddress = source.readAddress();
  return {
    $$type: 'GemzJetton$Data' as const,
    totalSupply: _totalSupply,
    owner: _owner,
    administrator: _administrator,
    content: _content,
    mintable: _mintable,
    max_supply: _max_supply,
    curveConfig: _curveConfig,
    txFee: _txFee,
    forward_ton_amount: _forward_ton_amount,
    dexJettonContractAddress: _dexJettonContractAddress,
    burnAddress: _burnAddress,
  };
}

function loadGetterTupleGemzJetton$Data(source: TupleReader) {
  let _totalSupply = source.readBigNumber();
  let _owner = source.readAddress();
  let _administrator = source.readAddress();
  let _content = source.readCell();
  let _mintable = source.readBoolean();
  let _max_supply = source.readBigNumber();
  const _curveConfig = loadGetterTupleCurveConfig(source);
  let _txFee = source.readBigNumber();
  let _forward_ton_amount = source.readBigNumber();
  let _dexJettonContractAddress = source.readAddressOpt();
  let _burnAddress = source.readAddress();
  return {
    $$type: 'GemzJetton$Data' as const,
    totalSupply: _totalSupply,
    owner: _owner,
    administrator: _administrator,
    content: _content,
    mintable: _mintable,
    max_supply: _max_supply,
    curveConfig: _curveConfig,
    txFee: _txFee,
    forward_ton_amount: _forward_ton_amount,
    dexJettonContractAddress: _dexJettonContractAddress,
    burnAddress: _burnAddress,
  };
}

function storeTupleGemzJetton$Data(source: GemzJetton$Data) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.totalSupply);
  builder.writeAddress(source.owner);
  builder.writeAddress(source.administrator);
  builder.writeCell(source.content);
  builder.writeBoolean(source.mintable);
  builder.writeNumber(source.max_supply);
  builder.writeTuple(storeTupleCurveConfig(source.curveConfig));
  builder.writeNumber(source.txFee);
  builder.writeNumber(source.forward_ton_amount);
  builder.writeAddress(source.dexJettonContractAddress);
  builder.writeAddress(source.burnAddress);
  return builder.build();
}

function dictValueParserGemzJetton$Data(): DictionaryValue<GemzJetton$Data> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeGemzJetton$Data(src)).endCell());
    },
    parse: (src) => {
      return loadGemzJetton$Data(src.loadRef().beginParse());
    },
  };
}

export type JettonData = {
  $$type: 'JettonData';
  totalSupply: bigint;
  mintable: boolean;
  owner: Address;
  content: Cell;
  walletCode: Cell;
};

export function storeJettonData(src: JettonData) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeInt(src.totalSupply, 257);
    b_0.storeBit(src.mintable);
    b_0.storeAddress(src.owner);
    b_0.storeRef(src.content);
    b_0.storeRef(src.walletCode);
  };
}

export function loadJettonData(slice: Slice) {
  let sc_0 = slice;
  let _totalSupply = sc_0.loadIntBig(257);
  let _mintable = sc_0.loadBit();
  let _owner = sc_0.loadAddress();
  let _content = sc_0.loadRef();
  let _walletCode = sc_0.loadRef();
  return {
    $$type: 'JettonData' as const,
    totalSupply: _totalSupply,
    mintable: _mintable,
    owner: _owner,
    content: _content,
    walletCode: _walletCode,
  };
}

function loadTupleJettonData(source: TupleReader) {
  let _totalSupply = source.readBigNumber();
  let _mintable = source.readBoolean();
  let _owner = source.readAddress();
  let _content = source.readCell();
  let _walletCode = source.readCell();
  return {
    $$type: 'JettonData' as const,
    totalSupply: _totalSupply,
    mintable: _mintable,
    owner: _owner,
    content: _content,
    walletCode: _walletCode,
  };
}

function loadGetterTupleJettonData(source: TupleReader) {
  let _totalSupply = source.readBigNumber();
  let _mintable = source.readBoolean();
  let _owner = source.readAddress();
  let _content = source.readCell();
  let _walletCode = source.readCell();
  return {
    $$type: 'JettonData' as const,
    totalSupply: _totalSupply,
    mintable: _mintable,
    owner: _owner,
    content: _content,
    walletCode: _walletCode,
  };
}

function storeTupleJettonData(source: JettonData) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.totalSupply);
  builder.writeBoolean(source.mintable);
  builder.writeAddress(source.owner);
  builder.writeCell(source.content);
  builder.writeCell(source.walletCode);
  return builder.build();
}

function dictValueParserJettonData(): DictionaryValue<JettonData> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeJettonData(src)).endCell());
    },
    parse: (src) => {
      return loadJettonData(src.loadRef().beginParse());
    },
  };
}

export type JettonDefaultWallet$Data = {
  $$type: 'JettonDefaultWallet$Data';
  balance: bigint;
  owner: Address;
  master: Address;
};

export function storeJettonDefaultWallet$Data(src: JettonDefaultWallet$Data) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeInt(src.balance, 257);
    b_0.storeAddress(src.owner);
    b_0.storeAddress(src.master);
  };
}

export function loadJettonDefaultWallet$Data(slice: Slice) {
  let sc_0 = slice;
  let _balance = sc_0.loadIntBig(257);
  let _owner = sc_0.loadAddress();
  let _master = sc_0.loadAddress();
  return {
    $$type: 'JettonDefaultWallet$Data' as const,
    balance: _balance,
    owner: _owner,
    master: _master,
  };
}

function loadTupleJettonDefaultWallet$Data(source: TupleReader) {
  let _balance = source.readBigNumber();
  let _owner = source.readAddress();
  let _master = source.readAddress();
  return {
    $$type: 'JettonDefaultWallet$Data' as const,
    balance: _balance,
    owner: _owner,
    master: _master,
  };
}

function loadGetterTupleJettonDefaultWallet$Data(source: TupleReader) {
  let _balance = source.readBigNumber();
  let _owner = source.readAddress();
  let _master = source.readAddress();
  return {
    $$type: 'JettonDefaultWallet$Data' as const,
    balance: _balance,
    owner: _owner,
    master: _master,
  };
}

function storeTupleJettonDefaultWallet$Data(source: JettonDefaultWallet$Data) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.balance);
  builder.writeAddress(source.owner);
  builder.writeAddress(source.master);
  return builder.build();
}

function dictValueParserJettonDefaultWallet$Data(): DictionaryValue<JettonDefaultWallet$Data> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeJettonDefaultWallet$Data(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadJettonDefaultWallet$Data(src.loadRef().beginParse());
    },
  };
}

export type GemzDexJetton$Data = {
  $$type: 'GemzDexJetton$Data';
  totalSupply: bigint;
  owner: Address;
  administrator: Address;
  content: Cell;
  mintable: boolean;
  max_supply: bigint;
};

export function storeGemzDexJetton$Data(src: GemzDexJetton$Data) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeCoins(src.totalSupply);
    b_0.storeAddress(src.owner);
    b_0.storeAddress(src.administrator);
    b_0.storeRef(src.content);
    b_0.storeBit(src.mintable);
    b_0.storeCoins(src.max_supply);
  };
}

export function loadGemzDexJetton$Data(slice: Slice) {
  let sc_0 = slice;
  let _totalSupply = sc_0.loadCoins();
  let _owner = sc_0.loadAddress();
  let _administrator = sc_0.loadAddress();
  let _content = sc_0.loadRef();
  let _mintable = sc_0.loadBit();
  let _max_supply = sc_0.loadCoins();
  return {
    $$type: 'GemzDexJetton$Data' as const,
    totalSupply: _totalSupply,
    owner: _owner,
    administrator: _administrator,
    content: _content,
    mintable: _mintable,
    max_supply: _max_supply,
  };
}

function loadTupleGemzDexJetton$Data(source: TupleReader) {
  let _totalSupply = source.readBigNumber();
  let _owner = source.readAddress();
  let _administrator = source.readAddress();
  let _content = source.readCell();
  let _mintable = source.readBoolean();
  let _max_supply = source.readBigNumber();
  return {
    $$type: 'GemzDexJetton$Data' as const,
    totalSupply: _totalSupply,
    owner: _owner,
    administrator: _administrator,
    content: _content,
    mintable: _mintable,
    max_supply: _max_supply,
  };
}

function loadGetterTupleGemzDexJetton$Data(source: TupleReader) {
  let _totalSupply = source.readBigNumber();
  let _owner = source.readAddress();
  let _administrator = source.readAddress();
  let _content = source.readCell();
  let _mintable = source.readBoolean();
  let _max_supply = source.readBigNumber();
  return {
    $$type: 'GemzDexJetton$Data' as const,
    totalSupply: _totalSupply,
    owner: _owner,
    administrator: _administrator,
    content: _content,
    mintable: _mintable,
    max_supply: _max_supply,
  };
}

function storeTupleGemzDexJetton$Data(source: GemzDexJetton$Data) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.totalSupply);
  builder.writeAddress(source.owner);
  builder.writeAddress(source.administrator);
  builder.writeCell(source.content);
  builder.writeBoolean(source.mintable);
  builder.writeNumber(source.max_supply);
  return builder.build();
}

function dictValueParserGemzDexJetton$Data(): DictionaryValue<GemzDexJetton$Data> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeGemzDexJetton$Data(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadGemzDexJetton$Data(src.loadRef().beginParse());
    },
  };
}

export type DexJettonData = {
  $$type: 'DexJettonData';
  totalSupply: bigint;
  mintable: boolean;
  owner: Address;
  content: Cell;
  walletCode: Cell;
};

export function storeDexJettonData(src: DexJettonData) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeInt(src.totalSupply, 257);
    b_0.storeBit(src.mintable);
    b_0.storeAddress(src.owner);
    b_0.storeRef(src.content);
    b_0.storeRef(src.walletCode);
  };
}

export function loadDexJettonData(slice: Slice) {
  let sc_0 = slice;
  let _totalSupply = sc_0.loadIntBig(257);
  let _mintable = sc_0.loadBit();
  let _owner = sc_0.loadAddress();
  let _content = sc_0.loadRef();
  let _walletCode = sc_0.loadRef();
  return {
    $$type: 'DexJettonData' as const,
    totalSupply: _totalSupply,
    mintable: _mintable,
    owner: _owner,
    content: _content,
    walletCode: _walletCode,
  };
}

function loadTupleDexJettonData(source: TupleReader) {
  let _totalSupply = source.readBigNumber();
  let _mintable = source.readBoolean();
  let _owner = source.readAddress();
  let _content = source.readCell();
  let _walletCode = source.readCell();
  return {
    $$type: 'DexJettonData' as const,
    totalSupply: _totalSupply,
    mintable: _mintable,
    owner: _owner,
    content: _content,
    walletCode: _walletCode,
  };
}

function loadGetterTupleDexJettonData(source: TupleReader) {
  let _totalSupply = source.readBigNumber();
  let _mintable = source.readBoolean();
  let _owner = source.readAddress();
  let _content = source.readCell();
  let _walletCode = source.readCell();
  return {
    $$type: 'DexJettonData' as const,
    totalSupply: _totalSupply,
    mintable: _mintable,
    owner: _owner,
    content: _content,
    walletCode: _walletCode,
  };
}

function storeTupleDexJettonData(source: DexJettonData) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.totalSupply);
  builder.writeBoolean(source.mintable);
  builder.writeAddress(source.owner);
  builder.writeCell(source.content);
  builder.writeCell(source.walletCode);
  return builder.build();
}

function dictValueParserDexJettonData(): DictionaryValue<DexJettonData> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeDexJettonData(src)).endCell());
    },
    parse: (src) => {
      return loadDexJettonData(src.loadRef().beginParse());
    },
  };
}

export type JettonDexDefaultWallet$Data = {
  $$type: 'JettonDexDefaultWallet$Data';
  balance: bigint;
  owner: Address;
  master: Address;
};

export function storeJettonDexDefaultWallet$Data(
  src: JettonDexDefaultWallet$Data,
) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeInt(src.balance, 257);
    b_0.storeAddress(src.owner);
    b_0.storeAddress(src.master);
  };
}

export function loadJettonDexDefaultWallet$Data(slice: Slice) {
  let sc_0 = slice;
  let _balance = sc_0.loadIntBig(257);
  let _owner = sc_0.loadAddress();
  let _master = sc_0.loadAddress();
  return {
    $$type: 'JettonDexDefaultWallet$Data' as const,
    balance: _balance,
    owner: _owner,
    master: _master,
  };
}

function loadTupleJettonDexDefaultWallet$Data(source: TupleReader) {
  let _balance = source.readBigNumber();
  let _owner = source.readAddress();
  let _master = source.readAddress();
  return {
    $$type: 'JettonDexDefaultWallet$Data' as const,
    balance: _balance,
    owner: _owner,
    master: _master,
  };
}

function loadGetterTupleJettonDexDefaultWallet$Data(source: TupleReader) {
  let _balance = source.readBigNumber();
  let _owner = source.readAddress();
  let _master = source.readAddress();
  return {
    $$type: 'JettonDexDefaultWallet$Data' as const,
    balance: _balance,
    owner: _owner,
    master: _master,
  };
}

function storeTupleJettonDexDefaultWallet$Data(
  source: JettonDexDefaultWallet$Data,
) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.balance);
  builder.writeAddress(source.owner);
  builder.writeAddress(source.master);
  return builder.build();
}

function dictValueParserJettonDexDefaultWallet$Data(): DictionaryValue<JettonDexDefaultWallet$Data> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeJettonDexDefaultWallet$Data(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadJettonDexDefaultWallet$Data(src.loadRef().beginParse());
    },
  };
}

export type JettonDeployNonce = {
  $$type: 'JettonDeployNonce';
  ownerAddress: Address;
  memeId: bigint;
  buyAmount: bigint;
  timestamp: string;
  seqno_k: bigint;
  seqno_v: bigint;
};

export function storeJettonDeployNonce(src: JettonDeployNonce) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeAddress(src.ownerAddress);
    b_0.storeInt(src.memeId, 257);
    b_0.storeInt(src.buyAmount, 257);
    b_0.storeStringRefTail(src.timestamp);
    let b_1 = new Builder();
    b_1.storeInt(src.seqno_k, 257);
    b_1.storeInt(src.seqno_v, 257);
    b_0.storeRef(b_1.endCell());
  };
}

export function loadJettonDeployNonce(slice: Slice) {
  let sc_0 = slice;
  let _ownerAddress = sc_0.loadAddress();
  let _memeId = sc_0.loadIntBig(257);
  let _buyAmount = sc_0.loadIntBig(257);
  let _timestamp = sc_0.loadStringRefTail();
  let sc_1 = sc_0.loadRef().beginParse();
  let _seqno_k = sc_1.loadIntBig(257);
  let _seqno_v = sc_1.loadIntBig(257);
  return {
    $$type: 'JettonDeployNonce' as const,
    ownerAddress: _ownerAddress,
    memeId: _memeId,
    buyAmount: _buyAmount,
    timestamp: _timestamp,
    seqno_k: _seqno_k,
    seqno_v: _seqno_v,
  };
}

function loadTupleJettonDeployNonce(source: TupleReader) {
  let _ownerAddress = source.readAddress();
  let _memeId = source.readBigNumber();
  let _buyAmount = source.readBigNumber();
  let _timestamp = source.readString();
  let _seqno_k = source.readBigNumber();
  let _seqno_v = source.readBigNumber();
  return {
    $$type: 'JettonDeployNonce' as const,
    ownerAddress: _ownerAddress,
    memeId: _memeId,
    buyAmount: _buyAmount,
    timestamp: _timestamp,
    seqno_k: _seqno_k,
    seqno_v: _seqno_v,
  };
}

function loadGetterTupleJettonDeployNonce(source: TupleReader) {
  let _ownerAddress = source.readAddress();
  let _memeId = source.readBigNumber();
  let _buyAmount = source.readBigNumber();
  let _timestamp = source.readString();
  let _seqno_k = source.readBigNumber();
  let _seqno_v = source.readBigNumber();
  return {
    $$type: 'JettonDeployNonce' as const,
    ownerAddress: _ownerAddress,
    memeId: _memeId,
    buyAmount: _buyAmount,
    timestamp: _timestamp,
    seqno_k: _seqno_k,
    seqno_v: _seqno_v,
  };
}

function storeTupleJettonDeployNonce(source: JettonDeployNonce) {
  let builder = new TupleBuilder();
  builder.writeAddress(source.ownerAddress);
  builder.writeNumber(source.memeId);
  builder.writeNumber(source.buyAmount);
  builder.writeString(source.timestamp);
  builder.writeNumber(source.seqno_k);
  builder.writeNumber(source.seqno_v);
  return builder.build();
}

function dictValueParserJettonDeployNonce(): DictionaryValue<JettonDeployNonce> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeJettonDeployNonce(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadJettonDeployNonce(src.loadRef().beginParse());
    },
  };
}

export type BuyTokenNonce = {
  $$type: 'BuyTokenNonce';
  ownerAddress: Address;
  memeId: bigint;
  amount: bigint;
  timestamp: string;
  seqno_k: bigint;
  seqno_v: bigint;
};

export function storeBuyTokenNonce(src: BuyTokenNonce) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeAddress(src.ownerAddress);
    b_0.storeInt(src.memeId, 257);
    b_0.storeInt(src.amount, 257);
    b_0.storeStringRefTail(src.timestamp);
    let b_1 = new Builder();
    b_1.storeInt(src.seqno_k, 257);
    b_1.storeInt(src.seqno_v, 257);
    b_0.storeRef(b_1.endCell());
  };
}

export function loadBuyTokenNonce(slice: Slice) {
  let sc_0 = slice;
  let _ownerAddress = sc_0.loadAddress();
  let _memeId = sc_0.loadIntBig(257);
  let _amount = sc_0.loadIntBig(257);
  let _timestamp = sc_0.loadStringRefTail();
  let sc_1 = sc_0.loadRef().beginParse();
  let _seqno_k = sc_1.loadIntBig(257);
  let _seqno_v = sc_1.loadIntBig(257);
  return {
    $$type: 'BuyTokenNonce' as const,
    ownerAddress: _ownerAddress,
    memeId: _memeId,
    amount: _amount,
    timestamp: _timestamp,
    seqno_k: _seqno_k,
    seqno_v: _seqno_v,
  };
}

function loadTupleBuyTokenNonce(source: TupleReader) {
  let _ownerAddress = source.readAddress();
  let _memeId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _timestamp = source.readString();
  let _seqno_k = source.readBigNumber();
  let _seqno_v = source.readBigNumber();
  return {
    $$type: 'BuyTokenNonce' as const,
    ownerAddress: _ownerAddress,
    memeId: _memeId,
    amount: _amount,
    timestamp: _timestamp,
    seqno_k: _seqno_k,
    seqno_v: _seqno_v,
  };
}

function loadGetterTupleBuyTokenNonce(source: TupleReader) {
  let _ownerAddress = source.readAddress();
  let _memeId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _timestamp = source.readString();
  let _seqno_k = source.readBigNumber();
  let _seqno_v = source.readBigNumber();
  return {
    $$type: 'BuyTokenNonce' as const,
    ownerAddress: _ownerAddress,
    memeId: _memeId,
    amount: _amount,
    timestamp: _timestamp,
    seqno_k: _seqno_k,
    seqno_v: _seqno_v,
  };
}

function storeTupleBuyTokenNonce(source: BuyTokenNonce) {
  let builder = new TupleBuilder();
  builder.writeAddress(source.ownerAddress);
  builder.writeNumber(source.memeId);
  builder.writeNumber(source.amount);
  builder.writeString(source.timestamp);
  builder.writeNumber(source.seqno_k);
  builder.writeNumber(source.seqno_v);
  return builder.build();
}

function dictValueParserBuyTokenNonce(): DictionaryValue<BuyTokenNonce> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeBuyTokenNonce(src)).endCell());
    },
    parse: (src) => {
      return loadBuyTokenNonce(src.loadRef().beginParse());
    },
  };
}

export type TransferAmountNonce = {
  $$type: 'TransferAmountNonce';
  memeId: bigint;
  amount: bigint;
  timestamp: string;
  seqno_k: bigint;
  seqno_v: bigint;
};

export function storeTransferAmountNonce(src: TransferAmountNonce) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeInt(src.memeId, 257);
    b_0.storeInt(src.amount, 257);
    b_0.storeStringRefTail(src.timestamp);
    b_0.storeInt(src.seqno_k, 257);
    let b_1 = new Builder();
    b_1.storeInt(src.seqno_v, 257);
    b_0.storeRef(b_1.endCell());
  };
}

export function loadTransferAmountNonce(slice: Slice) {
  let sc_0 = slice;
  let _memeId = sc_0.loadIntBig(257);
  let _amount = sc_0.loadIntBig(257);
  let _timestamp = sc_0.loadStringRefTail();
  let _seqno_k = sc_0.loadIntBig(257);
  let sc_1 = sc_0.loadRef().beginParse();
  let _seqno_v = sc_1.loadIntBig(257);
  return {
    $$type: 'TransferAmountNonce' as const,
    memeId: _memeId,
    amount: _amount,
    timestamp: _timestamp,
    seqno_k: _seqno_k,
    seqno_v: _seqno_v,
  };
}

function loadTupleTransferAmountNonce(source: TupleReader) {
  let _memeId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _timestamp = source.readString();
  let _seqno_k = source.readBigNumber();
  let _seqno_v = source.readBigNumber();
  return {
    $$type: 'TransferAmountNonce' as const,
    memeId: _memeId,
    amount: _amount,
    timestamp: _timestamp,
    seqno_k: _seqno_k,
    seqno_v: _seqno_v,
  };
}

function loadGetterTupleTransferAmountNonce(source: TupleReader) {
  let _memeId = source.readBigNumber();
  let _amount = source.readBigNumber();
  let _timestamp = source.readString();
  let _seqno_k = source.readBigNumber();
  let _seqno_v = source.readBigNumber();
  return {
    $$type: 'TransferAmountNonce' as const,
    memeId: _memeId,
    amount: _amount,
    timestamp: _timestamp,
    seqno_k: _seqno_k,
    seqno_v: _seqno_v,
  };
}

function storeTupleTransferAmountNonce(source: TransferAmountNonce) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.memeId);
  builder.writeNumber(source.amount);
  builder.writeString(source.timestamp);
  builder.writeNumber(source.seqno_k);
  builder.writeNumber(source.seqno_v);
  return builder.build();
}

function dictValueParserTransferAmountNonce(): DictionaryValue<TransferAmountNonce> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeTransferAmountNonce(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadTransferAmountNonce(src.loadRef().beginParse());
    },
  };
}

export type DexDeployNonce = {
  $$type: 'DexDeployNonce';
  ownerAddress: Address;
  memeId: bigint;
  timestamp: string;
  seqno_k: bigint;
  seqno_v: bigint;
};

export function storeDexDeployNonce(src: DexDeployNonce) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeAddress(src.ownerAddress);
    b_0.storeInt(src.memeId, 257);
    b_0.storeStringRefTail(src.timestamp);
    b_0.storeInt(src.seqno_k, 257);
    let b_1 = new Builder();
    b_1.storeInt(src.seqno_v, 257);
    b_0.storeRef(b_1.endCell());
  };
}

export function loadDexDeployNonce(slice: Slice) {
  let sc_0 = slice;
  let _ownerAddress = sc_0.loadAddress();
  let _memeId = sc_0.loadIntBig(257);
  let _timestamp = sc_0.loadStringRefTail();
  let _seqno_k = sc_0.loadIntBig(257);
  let sc_1 = sc_0.loadRef().beginParse();
  let _seqno_v = sc_1.loadIntBig(257);
  return {
    $$type: 'DexDeployNonce' as const,
    ownerAddress: _ownerAddress,
    memeId: _memeId,
    timestamp: _timestamp,
    seqno_k: _seqno_k,
    seqno_v: _seqno_v,
  };
}

function loadTupleDexDeployNonce(source: TupleReader) {
  let _ownerAddress = source.readAddress();
  let _memeId = source.readBigNumber();
  let _timestamp = source.readString();
  let _seqno_k = source.readBigNumber();
  let _seqno_v = source.readBigNumber();
  return {
    $$type: 'DexDeployNonce' as const,
    ownerAddress: _ownerAddress,
    memeId: _memeId,
    timestamp: _timestamp,
    seqno_k: _seqno_k,
    seqno_v: _seqno_v,
  };
}

function loadGetterTupleDexDeployNonce(source: TupleReader) {
  let _ownerAddress = source.readAddress();
  let _memeId = source.readBigNumber();
  let _timestamp = source.readString();
  let _seqno_k = source.readBigNumber();
  let _seqno_v = source.readBigNumber();
  return {
    $$type: 'DexDeployNonce' as const,
    ownerAddress: _ownerAddress,
    memeId: _memeId,
    timestamp: _timestamp,
    seqno_k: _seqno_k,
    seqno_v: _seqno_v,
  };
}

function storeTupleDexDeployNonce(source: DexDeployNonce) {
  let builder = new TupleBuilder();
  builder.writeAddress(source.ownerAddress);
  builder.writeNumber(source.memeId);
  builder.writeString(source.timestamp);
  builder.writeNumber(source.seqno_k);
  builder.writeNumber(source.seqno_v);
  return builder.build();
}

function dictValueParserDexDeployNonce(): DictionaryValue<DexDeployNonce> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeDexDeployNonce(src)).endCell());
    },
    parse: (src) => {
      return loadDexDeployNonce(src.loadRef().beginParse());
    },
  };
}

export type ChangeOwner = {
  $$type: 'ChangeOwner';
  queryId: bigint;
  newOwner: Address;
};

export function storeChangeOwner(src: ChangeOwner) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(2174598809, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeAddress(src.newOwner);
  };
}

export function loadChangeOwner(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 2174598809) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _newOwner = sc_0.loadAddress();
  return {
    $$type: 'ChangeOwner' as const,
    queryId: _queryId,
    newOwner: _newOwner,
  };
}

function loadTupleChangeOwner(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _newOwner = source.readAddress();
  return {
    $$type: 'ChangeOwner' as const,
    queryId: _queryId,
    newOwner: _newOwner,
  };
}

function loadGetterTupleChangeOwner(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _newOwner = source.readAddress();
  return {
    $$type: 'ChangeOwner' as const,
    queryId: _queryId,
    newOwner: _newOwner,
  };
}

function storeTupleChangeOwner(source: ChangeOwner) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeAddress(source.newOwner);
  return builder.build();
}

function dictValueParserChangeOwner(): DictionaryValue<ChangeOwner> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeChangeOwner(src)).endCell());
    },
    parse: (src) => {
      return loadChangeOwner(src.loadRef().beginParse());
    },
  };
}

export type ChangeOwnerOk = {
  $$type: 'ChangeOwnerOk';
  queryId: bigint;
  newOwner: Address;
};

export function storeChangeOwnerOk(src: ChangeOwnerOk) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeUint(846932810, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeAddress(src.newOwner);
  };
}

export function loadChangeOwnerOk(slice: Slice) {
  let sc_0 = slice;
  if (sc_0.loadUint(32) !== 846932810) {
    throw Error('Invalid prefix');
  }
  let _queryId = sc_0.loadUintBig(64);
  let _newOwner = sc_0.loadAddress();
  return {
    $$type: 'ChangeOwnerOk' as const,
    queryId: _queryId,
    newOwner: _newOwner,
  };
}

function loadTupleChangeOwnerOk(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _newOwner = source.readAddress();
  return {
    $$type: 'ChangeOwnerOk' as const,
    queryId: _queryId,
    newOwner: _newOwner,
  };
}

function loadGetterTupleChangeOwnerOk(source: TupleReader) {
  let _queryId = source.readBigNumber();
  let _newOwner = source.readAddress();
  return {
    $$type: 'ChangeOwnerOk' as const,
    queryId: _queryId,
    newOwner: _newOwner,
  };
}

function storeTupleChangeOwnerOk(source: ChangeOwnerOk) {
  let builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeAddress(source.newOwner);
  return builder.build();
}

function dictValueParserChangeOwnerOk(): DictionaryValue<ChangeOwnerOk> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeChangeOwnerOk(src)).endCell());
    },
    parse: (src) => {
      return loadChangeOwnerOk(src.loadRef().beginParse());
    },
  };
}

export type Administrator$Data = {
  $$type: 'Administrator$Data';
  owner: Address;
  publicKey: bigint;
  curveConfig: CurveConfig;
  txFee: bigint;
  ownerShare: bigint;
  seqnos: Dictionary<bigint, bigint>;
};

export function storeAdministrator$Data(src: Administrator$Data) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeAddress(src.owner);
    b_0.storeUint(src.publicKey, 256);
    let b_1 = new Builder();
    b_1.store(storeCurveConfig(src.curveConfig));
    let b_2 = new Builder();
    b_2.storeInt(src.txFee, 257);
    b_2.storeInt(src.ownerShare, 257);
    b_2.storeDict(
      src.seqnos,
      Dictionary.Keys.BigInt(257),
      Dictionary.Values.BigInt(257),
    );
    b_1.storeRef(b_2.endCell());
    b_0.storeRef(b_1.endCell());
  };
}

export function loadAdministrator$Data(slice: Slice) {
  let sc_0 = slice;
  let _owner = sc_0.loadAddress();
  let _publicKey = sc_0.loadUintBig(256);
  let sc_1 = sc_0.loadRef().beginParse();
  let _curveConfig = loadCurveConfig(sc_1);
  let sc_2 = sc_1.loadRef().beginParse();
  let _txFee = sc_2.loadIntBig(257);
  let _ownerShare = sc_2.loadIntBig(257);
  let _seqnos = Dictionary.load(
    Dictionary.Keys.BigInt(257),
    Dictionary.Values.BigInt(257),
    sc_2,
  );
  return {
    $$type: 'Administrator$Data' as const,
    owner: _owner,
    publicKey: _publicKey,
    curveConfig: _curveConfig,
    txFee: _txFee,
    ownerShare: _ownerShare,
    seqnos: _seqnos,
  };
}

function loadTupleAdministrator$Data(source: TupleReader) {
  let _owner = source.readAddress();
  let _publicKey = source.readBigNumber();
  const _curveConfig = loadTupleCurveConfig(source);
  let _txFee = source.readBigNumber();
  let _ownerShare = source.readBigNumber();
  let _seqnos = Dictionary.loadDirect(
    Dictionary.Keys.BigInt(257),
    Dictionary.Values.BigInt(257),
    source.readCellOpt(),
  );
  return {
    $$type: 'Administrator$Data' as const,
    owner: _owner,
    publicKey: _publicKey,
    curveConfig: _curveConfig,
    txFee: _txFee,
    ownerShare: _ownerShare,
    seqnos: _seqnos,
  };
}

function loadGetterTupleAdministrator$Data(source: TupleReader) {
  let _owner = source.readAddress();
  let _publicKey = source.readBigNumber();
  const _curveConfig = loadGetterTupleCurveConfig(source);
  let _txFee = source.readBigNumber();
  let _ownerShare = source.readBigNumber();
  let _seqnos = Dictionary.loadDirect(
    Dictionary.Keys.BigInt(257),
    Dictionary.Values.BigInt(257),
    source.readCellOpt(),
  );
  return {
    $$type: 'Administrator$Data' as const,
    owner: _owner,
    publicKey: _publicKey,
    curveConfig: _curveConfig,
    txFee: _txFee,
    ownerShare: _ownerShare,
    seqnos: _seqnos,
  };
}

function storeTupleAdministrator$Data(source: Administrator$Data) {
  let builder = new TupleBuilder();
  builder.writeAddress(source.owner);
  builder.writeNumber(source.publicKey);
  builder.writeTuple(storeTupleCurveConfig(source.curveConfig));
  builder.writeNumber(source.txFee);
  builder.writeNumber(source.ownerShare);
  builder.writeCell(
    source.seqnos.size > 0
      ? beginCell()
          .storeDictDirect(
            source.seqnos,
            Dictionary.Keys.BigInt(257),
            Dictionary.Values.BigInt(257),
          )
          .endCell()
      : null,
  );
  return builder.build();
}

function dictValueParserAdministrator$Data(): DictionaryValue<Administrator$Data> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeAdministrator$Data(src)).endCell(),
      );
    },
    parse: (src) => {
      return loadAdministrator$Data(src.loadRef().beginParse());
    },
  };
}

type Administrator_init_args = {
  $$type: 'Administrator_init_args';
  owner: Address;
  publicKey: bigint;
};

function initAdministrator_init_args(src: Administrator_init_args) {
  return (builder: Builder) => {
    let b_0 = builder;
    b_0.storeAddress(src.owner);
    b_0.storeInt(src.publicKey, 257);
  };
}

async function Administrator_init(owner: Address, publicKey: bigint) {
  const __code = Cell.fromBase64(
    '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',
  );
  const __system = Cell.fromBase64(
    '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',
  );
  let builder = beginCell();
  builder.storeRef(__system);
  builder.storeUint(0, 1);
  initAdministrator_init_args({
    $$type: 'Administrator_init_args',
    owner,
    publicKey,
  })(builder);
  const __data = builder.endCell();
  return { code: __code, data: __data };
}

const Administrator_errors: { [key: number]: { message: string } } = {
  2: { message: `Stack underflow` },
  3: { message: `Stack overflow` },
  4: { message: `Integer overflow` },
  5: { message: `Integer out of expected range` },
  6: { message: `Invalid opcode` },
  7: { message: `Type check error` },
  8: { message: `Cell overflow` },
  9: { message: `Cell underflow` },
  10: { message: `Dictionary error` },
  11: { message: `'Unknown' error` },
  12: { message: `Fatal error` },
  13: { message: `Out of gas error` },
  14: { message: `Virtualization error` },
  32: { message: `Action list is invalid` },
  33: { message: `Action list is too long` },
  34: { message: `Action is invalid or not supported` },
  35: { message: `Invalid source address in outbound message` },
  36: { message: `Invalid destination address in outbound message` },
  37: { message: `Not enough TON` },
  38: { message: `Not enough extra-currencies` },
  39: { message: `Outbound message does not fit into a cell after rewriting` },
  40: { message: `Cannot process a message` },
  41: { message: `Library reference is null` },
  42: { message: `Library change action error` },
  43: {
    message: `Exceeded maximum number of cells in the library or the maximum depth of the Merkle tree`,
  },
  50: { message: `Account state size exceeded limits` },
  128: { message: `Null reference exception` },
  129: { message: `Invalid serialization prefix` },
  130: { message: `Invalid incoming message` },
  131: { message: `Constraints error` },
  132: { message: `Access denied` },
  133: { message: `Contract stopped` },
  134: { message: `Invalid argument` },
  135: { message: `Code of a contract was not found` },
  136: { message: `Invalid address` },
  137: { message: `Masterchain support is not enabled for this contract` },
  4159: { message: `Invalid value!!` },
  4429: { message: `Invalid sender` },
  6898: { message: `The total supply will be overlapping.` },
  43422: { message: `Invalid value - Burn` },
  62972: { message: `Invalid balance` },
};

const Administrator_types: ABIType[] = [
  {
    name: 'StateInit',
    header: null,
    fields: [
      { name: 'code', type: { kind: 'simple', type: 'cell', optional: false } },
      { name: 'data', type: { kind: 'simple', type: 'cell', optional: false } },
    ],
  },
  {
    name: 'StdAddress',
    header: null,
    fields: [
      {
        name: 'workchain',
        type: { kind: 'simple', type: 'int', optional: false, format: 8 },
      },
      {
        name: 'address',
        type: { kind: 'simple', type: 'uint', optional: false, format: 256 },
      },
    ],
  },
  {
    name: 'VarAddress',
    header: null,
    fields: [
      {
        name: 'workchain',
        type: { kind: 'simple', type: 'int', optional: false, format: 32 },
      },
      {
        name: 'address',
        type: { kind: 'simple', type: 'slice', optional: false },
      },
    ],
  },
  {
    name: 'Context',
    header: null,
    fields: [
      {
        name: 'bounced',
        type: { kind: 'simple', type: 'bool', optional: false },
      },
      {
        name: 'sender',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'value',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      { name: 'raw', type: { kind: 'simple', type: 'slice', optional: false } },
    ],
  },
  {
    name: 'SendParameters',
    header: null,
    fields: [
      {
        name: 'bounce',
        type: { kind: 'simple', type: 'bool', optional: false },
      },
      {
        name: 'to',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'value',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'mode',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      { name: 'body', type: { kind: 'simple', type: 'cell', optional: true } },
      { name: 'code', type: { kind: 'simple', type: 'cell', optional: true } },
      { name: 'data', type: { kind: 'simple', type: 'cell', optional: true } },
    ],
  },
  {
    name: 'Deploy',
    header: 2490013878,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
    ],
  },
  {
    name: 'DeployOk',
    header: 2952335191,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
    ],
  },
  {
    name: 'FactoryDeploy',
    header: 1829761339,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'cashback',
        type: { kind: 'simple', type: 'address', optional: false },
      },
    ],
  },
  {
    name: 'TokenTransfer',
    header: 260734629,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'amount',
        type: {
          kind: 'simple',
          type: 'uint',
          optional: false,
          format: 'coins',
        },
      },
      {
        name: 'destination',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'response_destination',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'custom_payload',
        type: { kind: 'simple', type: 'cell', optional: true },
      },
      {
        name: 'forward_ton_amount',
        type: {
          kind: 'simple',
          type: 'uint',
          optional: false,
          format: 'coins',
        },
      },
      {
        name: 'forward_payload',
        type: {
          kind: 'simple',
          type: 'slice',
          optional: false,
          format: 'remainder',
        },
      },
    ],
  },
  {
    name: 'TokenTransferInternal',
    header: 395134233,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'amount',
        type: {
          kind: 'simple',
          type: 'uint',
          optional: false,
          format: 'coins',
        },
      },
      {
        name: 'from',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'response_destination',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'forward_ton_amount',
        type: {
          kind: 'simple',
          type: 'uint',
          optional: false,
          format: 'coins',
        },
      },
      {
        name: 'jettonContractAddress',
        type: { kind: 'simple', type: 'address', optional: true },
      },
      {
        name: 'forward_payload',
        type: {
          kind: 'simple',
          type: 'slice',
          optional: false,
          format: 'remainder',
        },
      },
    ],
  },
  {
    name: 'TokenNotification',
    header: 1935855772,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'amount',
        type: {
          kind: 'simple',
          type: 'uint',
          optional: false,
          format: 'coins',
        },
      },
      {
        name: 'from',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'forward_payload',
        type: {
          kind: 'simple',
          type: 'slice',
          optional: false,
          format: 'remainder',
        },
      },
    ],
  },
  {
    name: 'TransferNotification',
    header: 1935855773,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'amount',
        type: {
          kind: 'simple',
          type: 'uint',
          optional: false,
          format: 'coins',
        },
      },
      {
        name: 'sender',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'forwardPayload',
        type: { kind: 'simple', type: 'cell', optional: true },
      },
    ],
  },
  {
    name: 'TokenBurn',
    header: 1499400124,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'amount',
        type: {
          kind: 'simple',
          type: 'uint',
          optional: false,
          format: 'coins',
        },
      },
      {
        name: 'owner',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'response_destination',
        type: { kind: 'simple', type: 'address', optional: false },
      },
    ],
  },
  {
    name: 'TokenBurnForSell',
    header: 2401739732,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'amount',
        type: {
          kind: 'simple',
          type: 'uint',
          optional: false,
          format: 'coins',
        },
      },
      {
        name: 'owner',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'response_destination',
        type: { kind: 'simple', type: 'address', optional: false },
      },
    ],
  },
  {
    name: 'TokensBurnt',
    header: 2401739733,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'amount',
        type: {
          kind: 'simple',
          type: 'uint',
          optional: false,
          format: 'coins',
        },
      },
      {
        name: 'tokenOwner',
        type: { kind: 'simple', type: 'address', optional: false },
      },
    ],
  },
  {
    name: 'TokenBurnNotification',
    header: 2078119902,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'amount',
        type: {
          kind: 'simple',
          type: 'uint',
          optional: false,
          format: 'coins',
        },
      },
      {
        name: 'owner',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'response_destination',
        type: { kind: 'simple', type: 'address', optional: true },
      },
    ],
  },
  {
    name: 'TokenExcesses',
    header: 3576854235,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
    ],
  },
  {
    name: 'TokenRefundOnFail',
    header: 2401739734,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'reason',
        type: { kind: 'simple', type: 'string', optional: false },
      },
    ],
  },
  {
    name: 'ProvideWalletAddress',
    header: 745978227,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'ownerAddress',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'includeAddress',
        type: { kind: 'simple', type: 'bool', optional: false },
      },
    ],
  },
  {
    name: 'TakeWalletAddress',
    header: 3513996288,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'walletAddress',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'ownerAddress',
        type: { kind: 'simple', type: 'address', optional: true },
      },
    ],
  },
  {
    name: 'UpgradeContract',
    header: 2785044657,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      { name: 'code', type: { kind: 'simple', type: 'cell', optional: true } },
      {
        name: 'responseDestination',
        type: { kind: 'simple', type: 'address', optional: false },
      },
    ],
  },
  {
    name: 'AdminCallTokenTransfer',
    header: 1185764112,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'amount',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'jettonContract',
        type: { kind: 'simple', type: 'address', optional: false },
      },
    ],
  },
  {
    name: 'JettonWalletData',
    header: null,
    fields: [
      {
        name: 'balance',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'owner',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'master',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'walletCode',
        type: { kind: 'simple', type: 'cell', optional: false },
      },
    ],
  },
  {
    name: 'MintJetton',
    header: 2928867052,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'amount',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'receiver',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'sender',
        type: { kind: 'simple', type: 'address', optional: false },
      },
    ],
  },
  {
    name: 'MintJettonPublic',
    header: 2570012896,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'amount',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'sender',
        type: { kind: 'simple', type: 'address', optional: false },
      },
    ],
  },
  {
    name: 'SellJettonPublic',
    header: 3871468253,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'amount',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'sender',
        type: { kind: 'simple', type: 'address', optional: false },
      },
    ],
  },
  {
    name: 'ClaimJetton',
    header: 2570012897,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'amount',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'sender',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'jettonContractAddress',
        type: { kind: 'simple', type: 'address', optional: true },
      },
    ],
  },
  {
    name: 'AdminCallTransferJetton',
    header: 260734632,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'nonce',
        type: { kind: 'simple', type: 'cell', optional: false },
      },
      {
        name: 'signature',
        type: { kind: 'simple', type: 'slice', optional: false },
      },
      {
        name: 'metadata',
        type: { kind: 'simple', type: 'cell', optional: false },
      },
      {
        name: 'firstBuy',
        type: { kind: 'simple', type: 'bool', optional: true },
      },
    ],
  },
  {
    name: 'AdminCallClaimDexJetton',
    header: 260734631,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'nonce',
        type: { kind: 'simple', type: 'cell', optional: false },
      },
      {
        name: 'signature',
        type: { kind: 'simple', type: 'slice', optional: false },
      },
      {
        name: 'metadata',
        type: { kind: 'simple', type: 'cell', optional: false },
      },
      {
        name: 'jettonContractAddress',
        type: { kind: 'simple', type: 'address', optional: false },
      },
    ],
  },
  {
    name: 'AdminCallDailyClaimDexJetton',
    header: 260734630,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'nonce',
        type: { kind: 'simple', type: 'cell', optional: false },
      },
      {
        name: 'signature',
        type: { kind: 'simple', type: 'slice', optional: false },
      },
      {
        name: 'metadata',
        type: { kind: 'simple', type: 'cell', optional: false },
      },
    ],
  },
  {
    name: 'AdminCallSellJetton',
    header: 260734633,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'nonce',
        type: { kind: 'simple', type: 'cell', optional: false },
      },
      {
        name: 'signature',
        type: { kind: 'simple', type: 'slice', optional: false },
      },
      {
        name: 'metadata',
        type: { kind: 'simple', type: 'cell', optional: false },
      },
    ],
  },
  {
    name: 'TokenUpdateContent',
    header: 439303245,
    fields: [
      {
        name: 'content',
        type: { kind: 'simple', type: 'cell', optional: false },
      },
    ],
  },
  {
    name: 'UpdateAdministrator',
    header: 1533902479,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'newAdministrator',
        type: { kind: 'simple', type: 'address', optional: false },
      },
    ],
  },
  {
    name: 'UpdatePublicKey',
    header: 2618105391,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'publicKey',
        type: { kind: 'simple', type: 'uint', optional: false, format: 256 },
      },
    ],
  },
  {
    name: 'UpdateTxFee',
    header: 1062099563,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'txFee',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
    ],
  },
  {
    name: 'UpdateCurveConfig',
    header: 2106498826,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'curveConfig',
        type: { kind: 'simple', type: 'CurveConfig', optional: false },
      },
    ],
  },
  {
    name: 'UpdateDexJettonContractAddress',
    header: 1248541741,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'dexJettonContractAddress',
        type: { kind: 'simple', type: 'address', optional: false },
      },
    ],
  },
  {
    name: 'JettonDeploy',
    header: 3035222952,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'metadata',
        type: { kind: 'simple', type: 'cell', optional: false },
      },
      {
        name: 'nonce',
        type: { kind: 'simple', type: 'cell', optional: false },
      },
      {
        name: 'signature',
        type: { kind: 'simple', type: 'slice', optional: false },
      },
    ],
  },
  {
    name: 'JettonDexDeploy',
    header: 2750923441,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'metadata',
        type: { kind: 'simple', type: 'cell', optional: false },
      },
      {
        name: 'nonce',
        type: { kind: 'simple', type: 'cell', optional: false },
      },
      {
        name: 'signature',
        type: { kind: 'simple', type: 'slice', optional: false },
      },
    ],
  },
  {
    name: 'JettonDeployWithCode',
    header: 4152114271,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'owner',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      { name: 'code', type: { kind: 'simple', type: 'cell', optional: false } },
      { name: 'data', type: { kind: 'simple', type: 'cell', optional: false } },
      {
        name: 'jettonContractAddress',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'signature',
        type: { kind: 'simple', type: 'slice', optional: false },
      },
      {
        name: 'offchainId',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
    ],
  },
  {
    name: 'VerifyDataSignature',
    header: 2997937648,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      { name: 'data', type: { kind: 'simple', type: 'cell', optional: false } },
      {
        name: 'signature',
        type: { kind: 'simple', type: 'slice', optional: false },
      },
    ],
  },
  {
    name: 'Ping',
    header: 1267924010,
    fields: [
      {
        name: 'ping',
        type: { kind: 'simple', type: 'string', optional: false },
      },
    ],
  },
  {
    name: 'TransferClaimedJetton',
    header: 384998329,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'amount',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'jettonOwner',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'jettonContractAddress',
        type: { kind: 'simple', type: 'address', optional: false },
      },
    ],
  },
  {
    name: 'TokenClaimNotification',
    header: 1430958165,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'amount',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'jettonOwner',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'jettonContractAddress',
        type: { kind: 'simple', type: 'address', optional: false },
      },
    ],
  },
  {
    name: 'CurveConfig',
    header: null,
    fields: [
      {
        name: 'maxSupply',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'maxTargetPrice',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'initialPrice',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
    ],
  },
  {
    name: 'JettonContractConfig',
    header: null,
    fields: [
      {
        name: 'curveConfig',
        type: { kind: 'simple', type: 'CurveConfig', optional: false },
      },
      {
        name: 'administrator',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'txFee',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
    ],
  },
  {
    name: 'JettonDexContractConfig',
    header: null,
    fields: [
      {
        name: 'maxSupply',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'administrator',
        type: { kind: 'simple', type: 'address', optional: false },
      },
    ],
  },
  {
    name: 'MintAmount',
    header: null,
    fields: [
      {
        name: 'amount',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
    ],
  },
  {
    name: 'GemzJetton$Data',
    header: null,
    fields: [
      {
        name: 'totalSupply',
        type: {
          kind: 'simple',
          type: 'uint',
          optional: false,
          format: 'coins',
        },
      },
      {
        name: 'owner',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'administrator',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'content',
        type: { kind: 'simple', type: 'cell', optional: false },
      },
      {
        name: 'mintable',
        type: { kind: 'simple', type: 'bool', optional: false },
      },
      {
        name: 'max_supply',
        type: {
          kind: 'simple',
          type: 'uint',
          optional: false,
          format: 'coins',
        },
      },
      {
        name: 'curveConfig',
        type: { kind: 'simple', type: 'CurveConfig', optional: false },
      },
      {
        name: 'txFee',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'forward_ton_amount',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'dexJettonContractAddress',
        type: { kind: 'simple', type: 'address', optional: true },
      },
      {
        name: 'burnAddress',
        type: { kind: 'simple', type: 'address', optional: false },
      },
    ],
  },
  {
    name: 'JettonData',
    header: null,
    fields: [
      {
        name: 'totalSupply',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'mintable',
        type: { kind: 'simple', type: 'bool', optional: false },
      },
      {
        name: 'owner',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'content',
        type: { kind: 'simple', type: 'cell', optional: false },
      },
      {
        name: 'walletCode',
        type: { kind: 'simple', type: 'cell', optional: false },
      },
    ],
  },
  {
    name: 'JettonDefaultWallet$Data',
    header: null,
    fields: [
      {
        name: 'balance',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'owner',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'master',
        type: { kind: 'simple', type: 'address', optional: false },
      },
    ],
  },
  {
    name: 'GemzDexJetton$Data',
    header: null,
    fields: [
      {
        name: 'totalSupply',
        type: {
          kind: 'simple',
          type: 'uint',
          optional: false,
          format: 'coins',
        },
      },
      {
        name: 'owner',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'administrator',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'content',
        type: { kind: 'simple', type: 'cell', optional: false },
      },
      {
        name: 'mintable',
        type: { kind: 'simple', type: 'bool', optional: false },
      },
      {
        name: 'max_supply',
        type: {
          kind: 'simple',
          type: 'uint',
          optional: false,
          format: 'coins',
        },
      },
    ],
  },
  {
    name: 'DexJettonData',
    header: null,
    fields: [
      {
        name: 'totalSupply',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'mintable',
        type: { kind: 'simple', type: 'bool', optional: false },
      },
      {
        name: 'owner',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'content',
        type: { kind: 'simple', type: 'cell', optional: false },
      },
      {
        name: 'walletCode',
        type: { kind: 'simple', type: 'cell', optional: false },
      },
    ],
  },
  {
    name: 'JettonDexDefaultWallet$Data',
    header: null,
    fields: [
      {
        name: 'balance',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'owner',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'master',
        type: { kind: 'simple', type: 'address', optional: false },
      },
    ],
  },
  {
    name: 'JettonDeployNonce',
    header: null,
    fields: [
      {
        name: 'ownerAddress',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'memeId',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'buyAmount',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'timestamp',
        type: { kind: 'simple', type: 'string', optional: false },
      },
      {
        name: 'seqno_k',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'seqno_v',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
    ],
  },
  {
    name: 'BuyTokenNonce',
    header: null,
    fields: [
      {
        name: 'ownerAddress',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'memeId',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'amount',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'timestamp',
        type: { kind: 'simple', type: 'string', optional: false },
      },
      {
        name: 'seqno_k',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'seqno_v',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
    ],
  },
  {
    name: 'TransferAmountNonce',
    header: null,
    fields: [
      {
        name: 'memeId',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'amount',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'timestamp',
        type: { kind: 'simple', type: 'string', optional: false },
      },
      {
        name: 'seqno_k',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'seqno_v',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
    ],
  },
  {
    name: 'DexDeployNonce',
    header: null,
    fields: [
      {
        name: 'ownerAddress',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'memeId',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'timestamp',
        type: { kind: 'simple', type: 'string', optional: false },
      },
      {
        name: 'seqno_k',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'seqno_v',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
    ],
  },
  {
    name: 'ChangeOwner',
    header: 2174598809,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'newOwner',
        type: { kind: 'simple', type: 'address', optional: false },
      },
    ],
  },
  {
    name: 'ChangeOwnerOk',
    header: 846932810,
    fields: [
      {
        name: 'queryId',
        type: { kind: 'simple', type: 'uint', optional: false, format: 64 },
      },
      {
        name: 'newOwner',
        type: { kind: 'simple', type: 'address', optional: false },
      },
    ],
  },
  {
    name: 'Administrator$Data',
    header: null,
    fields: [
      {
        name: 'owner',
        type: { kind: 'simple', type: 'address', optional: false },
      },
      {
        name: 'publicKey',
        type: { kind: 'simple', type: 'uint', optional: false, format: 256 },
      },
      {
        name: 'curveConfig',
        type: { kind: 'simple', type: 'CurveConfig', optional: false },
      },
      {
        name: 'txFee',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      {
        name: 'ownerShare',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
      { name: 'seqnos', type: { kind: 'dict', key: 'int', value: 'int' } },
    ],
  },
];

const Administrator_getters: ABIGetter[] = [
  {
    name: 'getJettonContractAddress',
    arguments: [
      {
        name: 'metadata',
        type: { kind: 'simple', type: 'cell', optional: false },
      },
    ],
    returnType: { kind: 'simple', type: 'address', optional: false },
  },
  {
    name: 'getDexJettonContractAddress',
    arguments: [
      {
        name: 'metadata',
        type: { kind: 'simple', type: 'cell', optional: false },
      },
    ],
    returnType: { kind: 'simple', type: 'address', optional: false },
  },
  {
    name: 'getSeqNo',
    arguments: [
      {
        name: 'index',
        type: { kind: 'simple', type: 'int', optional: false, format: 257 },
      },
    ],
    returnType: { kind: 'simple', type: 'int', optional: true, format: 257 },
  },
  {
    name: 'version',
    arguments: [],
    returnType: { kind: 'simple', type: 'string', optional: false },
  },
  {
    name: 'owner',
    arguments: [],
    returnType: { kind: 'simple', type: 'address', optional: false },
  },
];

export const Administrator_getterMapping: { [key: string]: string } = {
  getJettonContractAddress: 'getGetJettonContractAddress',
  getDexJettonContractAddress: 'getGetDexJettonContractAddress',
  getSeqNo: 'getGetSeqNo',
  version: 'getVersion',
  owner: 'getOwner',
};

const Administrator_receivers: ABIReceiver[] = [
  {
    receiver: 'internal',
    message: { kind: 'typed', type: 'UpdateCurveConfig' },
  },
  { receiver: 'internal', message: { kind: 'typed', type: 'UpdatePublicKey' } },
  { receiver: 'internal', message: { kind: 'typed', type: 'UpdateTxFee' } },
  { receiver: 'internal', message: { kind: 'typed', type: 'JettonDeploy' } },
  { receiver: 'internal', message: { kind: 'typed', type: 'JettonDexDeploy' } },
  {
    receiver: 'internal',
    message: { kind: 'typed', type: 'AdminCallTransferJetton' },
  },
  {
    receiver: 'internal',
    message: { kind: 'typed', type: 'AdminCallClaimDexJetton' },
  },
  {
    receiver: 'internal',
    message: { kind: 'typed', type: 'AdminCallDailyClaimDexJetton' },
  },
  {
    receiver: 'internal',
    message: { kind: 'typed', type: 'AdminCallSellJetton' },
  },
  { receiver: 'internal', message: { kind: 'text', text: 'identify' } },
  { receiver: 'internal', message: { kind: 'text', text: 'withdraw' } },
  { receiver: 'internal', message: { kind: 'typed', type: 'Deploy' } },
  { receiver: 'internal', message: { kind: 'typed', type: 'UpgradeContract' } },
];

export class Administrator implements Contract {
  static async init(owner: Address, publicKey: bigint) {
    return await Administrator_init(owner, publicKey);
  }

  static async fromInit(owner: Address, publicKey: bigint) {
    const init = await Administrator_init(owner, publicKey);
    const address = contractAddress(0, init);
    return new Administrator(address, init);
  }

  static fromAddress(address: Address) {
    return new Administrator(address);
  }

  readonly address: Address;
  readonly init?: { code: Cell; data: Cell };
  readonly abi: ContractABI = {
    types: Administrator_types,
    getters: Administrator_getters,
    receivers: Administrator_receivers,
    errors: Administrator_errors,
  };

  private constructor(address: Address, init?: { code: Cell; data: Cell }) {
    this.address = address;
    this.init = init;
  }

  async send(
    provider: ContractProvider,
    via: Sender,
    args: { value: bigint; bounce?: boolean | null | undefined },
    message:
      | UpdateCurveConfig
      | UpdatePublicKey
      | UpdateTxFee
      | JettonDeploy
      | JettonDexDeploy
      | AdminCallTransferJetton
      | AdminCallClaimDexJetton
      | AdminCallDailyClaimDexJetton
      | AdminCallSellJetton
      | 'identify'
      | 'withdraw'
      | Deploy
      | UpgradeContract,
  ) {
    let body: Cell | null = null;
    if (
      message &&
      typeof message === 'object' &&
      !(message instanceof Slice) &&
      message.$$type === 'UpdateCurveConfig'
    ) {
      body = beginCell().store(storeUpdateCurveConfig(message)).endCell();
    }
    if (
      message &&
      typeof message === 'object' &&
      !(message instanceof Slice) &&
      message.$$type === 'UpdatePublicKey'
    ) {
      body = beginCell().store(storeUpdatePublicKey(message)).endCell();
    }
    if (
      message &&
      typeof message === 'object' &&
      !(message instanceof Slice) &&
      message.$$type === 'UpdateTxFee'
    ) {
      body = beginCell().store(storeUpdateTxFee(message)).endCell();
    }
    if (
      message &&
      typeof message === 'object' &&
      !(message instanceof Slice) &&
      message.$$type === 'JettonDeploy'
    ) {
      body = beginCell().store(storeJettonDeploy(message)).endCell();
    }
    if (
      message &&
      typeof message === 'object' &&
      !(message instanceof Slice) &&
      message.$$type === 'JettonDexDeploy'
    ) {
      body = beginCell().store(storeJettonDexDeploy(message)).endCell();
    }
    if (
      message &&
      typeof message === 'object' &&
      !(message instanceof Slice) &&
      message.$$type === 'AdminCallTransferJetton'
    ) {
      body = beginCell().store(storeAdminCallTransferJetton(message)).endCell();
    }
    if (
      message &&
      typeof message === 'object' &&
      !(message instanceof Slice) &&
      message.$$type === 'AdminCallClaimDexJetton'
    ) {
      body = beginCell().store(storeAdminCallClaimDexJetton(message)).endCell();
    }
    if (
      message &&
      typeof message === 'object' &&
      !(message instanceof Slice) &&
      message.$$type === 'AdminCallDailyClaimDexJetton'
    ) {
      body = beginCell()
        .store(storeAdminCallDailyClaimDexJetton(message))
        .endCell();
    }
    if (
      message &&
      typeof message === 'object' &&
      !(message instanceof Slice) &&
      message.$$type === 'AdminCallSellJetton'
    ) {
      body = beginCell().store(storeAdminCallSellJetton(message)).endCell();
    }
    if (message === 'identify') {
      body = beginCell().storeUint(0, 32).storeStringTail(message).endCell();
    }
    if (message === 'withdraw') {
      body = beginCell().storeUint(0, 32).storeStringTail(message).endCell();
    }
    if (
      message &&
      typeof message === 'object' &&
      !(message instanceof Slice) &&
      message.$$type === 'Deploy'
    ) {
      body = beginCell().store(storeDeploy(message)).endCell();
    }
    if (
      message &&
      typeof message === 'object' &&
      !(message instanceof Slice) &&
      message.$$type === 'UpgradeContract'
    ) {
      body = beginCell().store(storeUpgradeContract(message)).endCell();
    }
    if (body === null) {
      throw new Error('Invalid message type');
    }

    await provider.internal(via, { ...args, body: body });
  }

  async getGetJettonContractAddress(
    provider: ContractProvider,
    metadata: Cell,
  ) {
    let builder = new TupleBuilder();
    builder.writeCell(metadata);
    let source = (
      await provider.get('getJettonContractAddress', builder.build())
    ).stack;
    let result = source.readAddress();
    return result;
  }

  async getGetDexJettonContractAddress(
    provider: ContractProvider,
    metadata: Cell,
  ) {
    let builder = new TupleBuilder();
    builder.writeCell(metadata);
    let source = (
      await provider.get('getDexJettonContractAddress', builder.build())
    ).stack;
    let result = source.readAddress();
    return result;
  }

  async getGetSeqNo(provider: ContractProvider, index: bigint) {
    let builder = new TupleBuilder();
    builder.writeNumber(index);
    let source = (await provider.get('getSeqNo', builder.build())).stack;
    let result = source.readBigNumberOpt();
    return result;
  }

  async getVersion(provider: ContractProvider) {
    let builder = new TupleBuilder();
    let source = (await provider.get('version', builder.build())).stack;
    let result = source.readString();
    return result;
  }

  async getOwner(provider: ContractProvider) {
    let builder = new TupleBuilder();
    let source = (await provider.get('owner', builder.build())).stack;
    let result = source.readAddress();
    return result;
  }
}
